/**
 * The estimate as the customer reads it.
 *
 * A rep hands this over on a phone at a kitchen table, so it is one column that
 * scrolls once, in a fixed order: who this is for, what we found on the roof,
 * how we will build it, the packages, what can be added, the money, and the
 * agreement. Everything on it is built here rather than in a component, for the
 * usual reason (rule 5) and one specific to this document: **the customer never
 * sees a square count.**
 *
 * Squares are how the company prices a roof and how the crew is paid; to a
 * homeowner they are a unit that invites arithmetic — "you charged me how much
 * a square?" — against a number they have no way to check. They stay on the job
 * for production. `squareCountLeaks()` below is the guard, and it is asserted
 * over a whole rendered document rather than trusted to whoever writes the next
 * section.
 *
 * The document is a value, not a view: `buildPresentedDocument()` returns
 * exactly what gets snapshotted into `proposals.snapshot` at send. From that
 * moment the customer's copy is that JSON, not this code — edit the job all you
 * like, the thing they were sent does not move.
 *
 * ## The 2026-08-17 rebuild
 *
 * Rebuilt to the design Jonathon worked up with Fable
 * (`docs/PROPOSALS-PLAN.md`). Four things changed in the value, not just in the
 * drawing of it:
 *
 *  1. **A section the rep turned off is not in the document at all.** There is
 *     no "hidden" flag for a renderer to honour or forget. What was sent is
 *     what is there.
 *  2. **Every document is a list of packages**, even when it offers one. The
 *     old fork — one price meant `system` + `warranty` + `investment`, several
 *     meant `packages` — is gone, and with it three sections.
 *  3. **"What could change the price", "Why us", "What happens next" and "Our
 *     paperwork" are gone from the customer's copy** (Jonathon, 2026-08-17).
 *     The decking and extra-layer charges they carried are in the agreement,
 *     which is the copy that binds anybody.
 *  4. **The customer reads "agreement", never "contract".** The section id is
 *     still `contract`, because that is what the signing path, the acceptance
 *     record and every frozen snapshot call it; only the words changed.
 */

import {
  contractBlocks,
  contractKindFor,
  isRepairTrade,
  isReplacementTrade,
  showsInsuranceTerms,
  warrantySectionFor,
  COMPANY_SIGNER_NAME,
  contractVersionFor,
  type ContractBlock,
  type ContractKind,
  type ContractValues,
  type LongerWorkmanship,
} from "./contract.js";
import { isCommercialJob } from "./autoExpenses.js";
import { depositFor, type Deposit } from "./deposit.js";
import {
  FINANCING_MAX_TOTAL,
  FINANCING_MIN_TOTAL,
  defaultFinancingProgram,
  financingAddition,
  financingPayment,
  financingPrograms,
  formatApr,
  formatTerm,
  type FinancingProgram,
  type FinancingTerms,
} from "./financing.js";
import { addCents, formatMoney, toCents, type Cents } from "./money.js";
import {
  planProblem,
  planTermsWording,
  planWording,
  type PlanTerms,
} from "./paymentPlan.js";
import { GUTTER_COLORS, gutterColorsApply } from "./gutterColors.js";
import { upgradeVideoFor, type UpgradeVideo } from "./upgradeVideos.js";
import {
  PRO_FLO_SHOWCASE,
  isGutterGuardProduct,
  type ProductShowcase,
} from "./productShowcases.js";
import { sidingLineFor, type SidingLine } from "./sidingColors.js";
import {
  buildComparison,
  type ChartEdits,
  type ComparedPackage,
} from "./packageComparison.js";
import {
  SHINGLE_COLOR_NOTE,
  shingleLineFor,
  type ColorViewer,
  type ShingleColor,
} from "./shingleColors.js";
import {
  packageShingleDifferentiator,
  packageShingleTerms,
  withShingleWarrantyYears,
} from "./shingleTerms.js";
import {
  SCOPE_OF_WORK_CLOSER,
  SCOPE_OF_WORK_STEPS,
  scopeOfWorkApplies,
  type QuotedRoof,
  type ScopeCloser,
  type ScopeStep,
} from "./scopeOfWork.js";
import { wordingFor, wordingForOptional } from "./commercialWording.js";
import { measuredQuantity } from "./measuredQuantity.js";
import type { GeneratorProduct, RoofLayouts } from "./generator.js";
import {
  FINAL_SLOT,
  finalPayment,
  PAYMENT_SPLIT_THRESHOLD,
  signingFirstPaymentTyped,
  typedFirstPayment,
  type StoredPayment,
} from "./paymentSchedule.js";
import {
  defaultTriggerText,
  frozenPayments,
  paymentsAreCustom,
  type FrozenPayment,
} from "./frozenPayments.js";
import { PAYMENT_SLOT_LABEL } from "./constants.js";
import {
  anyTwoTogether,
  isWholeRoofOption,
  roofsBuyableTogether,
  type RoofOption,
} from "./multiOption.js";

/** A photo on the document. */
export interface DocumentPhoto {
  url: string;
  caption?: string | null;
}

export interface PresentedSection {
  id:
    | "cover"
    | "findings"
    | "videos"
    | "scope"
    | "packages"
    | "compare"
    | "showcase"
    | "upgrades"
    | "summary"
    | "financing"
    | "colors"
    | "work"
    | "contract";
  heading: string;
}

/**
 * Who this is for, who it is from, and the house it is about.
 *
 * The photograph is optional and off by default: it is uploaded per estimate,
 * and until somebody uploads one the cover reads better with no photo than
 * with a grey box (Jonathon, 2026-08-17). Turning it off hides the photograph
 * only — the names, the address and the licence number stay.
 */
export interface CoverSection extends PresentedSection {
  id: "cover";
  date: string;
  customerName: string;
  customerEmail: string | null;
  customerPhone: string | null;
  propertyAddress: string;
  estimateNumber: string;
  repName: string | null;
  repEmail: string | null;
  companyName: string;
  companyPhone: string | null;
  /**
   * Gone from the cover, and not replaced.
   *
   * Jonathon, 2026-08-21: "make sure the license number doesn't show
   * anywhere." The contract has never printed one (ADR 0037 and the note at
   * the top of `contract.ts`); the cover did, and that was the last of it. The
   * number stays in `company_settings` for the licence document itself.
   *
   * The key is kept, always null, because every proposal frozen before today
   * carries it and the customer's copy is read back through this type.
   */
  licenseNumber: null;
  /** Null when there is none, or when the rep turned it off. */
  coverPhotoUrl: string | null;
}

export interface FindingsSection extends PresentedSection {
  id: "findings";
  photos: DocumentPhoto[];
}

/**
 * One walkthrough of the work, for a repair the customer has never watched
 * anybody do.
 *
 * Jonathon, 2026-09-04: "i want these videos to populate on the appropriate
 * repair estimate (dryer for dryer. pipeboot for pipeboot.. heat stack for
 * heat stack). i want it to populate on the presenting screen right underneath
 * the photo section."
 *
 * A pipe boot, a dryer vent and a heat stack are each a few hundred dollars to
 * replace and none of them means anything to a homeowner, who cannot see the
 * thing being quoted and has no idea whether it is twenty minutes' work or a
 * day's. The photographs above show what is wrong; this shows what we do about
 * it. Directly under them, on purpose — the argument runs problem, then fix.
 *
 * The video belongs to the **product**, not the estimate. Every estimate
 * carrying a pipe boot replacement gets the same walkthrough with nothing for
 * a rep to attach, and improving the video improves every future proposal.
 */
export interface ProductVideo {
  /**
   * A public URL, not a storage path — the one asset on a customer's page that
   * is deliberately not signed.
   *
   * Their roof photographs are private and their links expire (migration
   * 20260813150000). This is our own instructional video, byte for byte the
   * same for every customer we ever send it to, and there is nothing in it to
   * protect. Signing it would buy no privacy and cost a broken video on any
   * copy opened more than six hours after it was fetched.
   */
  url: string;
  /** Shown before play and after the video ends. */
  posterUrl: string | null;
  /** "How we replace a pipe boot" — a heading, not the catalogue name. */
  title: string;
  /** The sentence under it. Null when the video speaks for itself. */
  caption: string | null;
}

export interface VideosSection extends PresentedSection {
  id: "videos";
  videos: ProductVideo[];
}

/** First of each, order kept. */
function dedupeBy<T>(items: readonly T[], key: (item: T) => string): T[] {
  const seen = new Set<string>();
  const kept: T[] = [];
  for (const item of items) {
    const k = key(item);
    if (seen.has(k)) continue;
    seen.add(k);
    kept.push(item);
  }
  return kept;
}

/**
 * How the roof gets built, in eight steps, under the photographs of the old one.
 *
 * Only on a full shingle replacement — see `scopeOfWork.ts` for which roofs
 * qualify and why the rest do not.
 */
export interface ScopeSection extends PresentedSection {
  id: "scope";
  steps: ScopeStep[];
  closer: ScopeCloser;
}

/**
 * One heading inside a package, and the lines under it.
 *
 * "What You Get", "Warranty", "Worry Free Wood Bonus" — the blocks the owner
 * writes and edits as products on the estimate (migration 20260809220000, and
 * `estimate_items.section` is the heading each line sits under). The document
 * copies whatever the lines say, which is what makes the wording editable
 * without a deploy.
 */
export interface PackageBlock {
  heading: string;
  lines: { name: string; body: string | null }[];
}

/**
 * One package the customer can pick between.
 *
 * A package **is an `estimates` row** — that standing decision is what makes
 * five packages cheap rather than a parallel universe of pricing. The ladder,
 * the payment slots and the trade trump all come along with it, and the
 * estimate the customer picks is the one that gets approved. `estimateId` is
 * therefore not decoration: it is what comes back on the signature, and what
 * the server checks against the options the proposal actually offered.
 */
export interface PresentedPackage {
  estimateId: string;
  /** "Bulletproof Package", "Bare Bones Package". */
  name: string;
  /** The one line underneath the name. Roofr calls it the Key Differentiator. */
  headline: string | null;
  total: Cents;
  totalFormatted: string;
  /** "or about $445/mo", or null when the rep turned payments off. */
  monthly: string | null;
  /** What this package is, in the owner's words. */
  blocks: PackageBlock[];
  /** At most one, and nothing enforces that here — see `buildPresentedDocument`. */
  recommended: boolean;
  /**
   * A whole roof going on the house (`isWholeRoofOption`: its trade, or its
   * lines' trades when it has none).
   *
   * Frozen so that a document which lets the customer buy several things can
   * still refuse two roofs: a roof and the gutters add up, two roof systems on
   * one house are rivals unless the rep said otherwise (ADR 0120,
   * `roofsTogether`). Absent on every other package, and on every document
   * frozen before 2026-09-10.
   *
   * Also the only packages that carry the Key Differentiator (ADR 0124).
   */
  wholeRoof?: boolean;
  /**
   * The first payment the rep typed on this estimate, as it stood when the
   * document was frozen (`typedFirstPayment`) — zero included, because zero is
   * a decision.
   *
   * Frozen so the deposit the customer reads is the one signing bills:
   * `presentedDeposit` runs it through `signingFirstPaymentTyped`, the rule
   * `signingFirstPayment` applies to the live schedule when they sign.
   *
   * Null when nothing was typed. Absent on every document frozen before
   * 2026-09-11, and the two are told apart on purpose:
   *
   *  - **Printing** reads both as the default rule, which is exactly what
   *    those older documents printed, so they go on printing it (ADR 0032).
   *  - **Billing** reads null as "nothing typed", but absent as "this
   *    document froze nothing" and goes to the live schedule
   *    (`typedFirstPaymentAtSigning`) — which is what signing always billed
   *    those documents by.
   *
   * So signing bills what the customer read. A rep who edits the first payment
   * after presenting changes neither the document nor the bill; presenting
   * again changes both.
   */
  typedFirstPayment?: Cents | null;
  /**
   * The rep's whole payment schedule as the document froze it: the three
   * typed amounts and the wording on all four slots (`frozenPayments`).
   *
   * Frozen so the schedule the customer reads is the one the rep wrote when
   * they sent it — `presentedSchedule` lays it out against what they chose,
   * with the Final as the remainder (ADR 0031, ADR 0267). Absent on every
   * document frozen before 2026-10-05, which printed no schedule and goes on
   * printing none (ADR 0032).
   */
  schedule?: FrozenPayment[];
  /**
   * The in-house payment plan the rep set up, frozen with the schedule
   * (ADR 0286). Absent on every document frozen before 2026-10-14, and on
   * every estimate with no plan.
   */
  plan?: PlanTerms;
}

export interface PackagesSection extends PresentedSection {
  id: "packages";
  packages: PresentedPackage[];
  /**
   * Whether the customer may buy more than one of these.
   *
   * `jobs.multi_option` (ADR 0030) as it stood when the document was frozen,
   * and only where at least two of the packages could be bought together.
   * Off, the packages are rival quotes for one roof and picking one un-picks
   * the last. On, they are separate pieces of work — a roof and the gutters,
   * the siding — and they add up. Whole roofs stay rivals of each other even
   * then (`wholeRoof`, `toggleChoice`).
   *
   * Frozen into the document rather than read at signing time, like everything
   * else here (ADR 0032): what the customer was offered is a fact about the
   * document they are reading, not about a checkbox somebody may since have
   * changed. Absent on every document sent before 2026-09-09, which is why it
   * is optional and why absent means one.
   */
  chooseMany?: boolean;
  /**
   * Whether two whole roofs may be bought together on this document.
   *
   * Only where a person set the job's box by hand (`roofsBuyableTogether`) —
   * a house and a detached garage, sold on one document (Jonathon,
   * 2026-09-10; ADR 0124). Only ever written beside `chooseMany`. Frozen like
   * it, and absent on every document sent before 2026-09-10, which keeps the
   * rule it went out under: two roofs are rivals.
   */
  roofsTogether?: boolean;
  /**
   * Which bought package is the main one, as estimate ids, first to last.
   *
   * When a customer buys several, the first of them in this order is the main
   * estimate: the upgrades land on it and its payment schedule is the one
   * used. That is version order (`mainEstimateOrder`), whatever order the
   * packages are read in, so a rep moving an option up the page never moves
   * the money (ADR 0294). Only ever written beside `chooseMany`, the only
   * documents where more than one can be bought. Absent on every document
   * frozen before 2026-10-22, whose packages were already in version order,
   * so their own order is the same answer.
   */
  leadOrder?: string[];
}

/**
 * One upgrade, offered once, under all of the packages.
 *
 * `includedIn` is the whole trick. Some packages already cover an upgrade —
 * Bulletproof's price includes the high temperature ice and water, Peace of
 * Mind's includes the pivot pipe boots — so the same tick box has to read as
 * "Included" and cost nothing on those packages, and as a price on the rest.
 * Which is which is decided when the document is built and frozen into it,
 * because it is a fact about what was quoted, not about what a template says
 * today.
 */
export interface PresentedUpgrade {
  /** Stable across a document. The product's name. */
  key: string;
  name: string;
  /** What it says to the customer. */
  blurb: string | null;
  price: Cents;
  priceFormatted: string;
  /** "approx $45 more a month", or null when it rounds to nothing. */
  monthly: string | null;
  /** Estimate ids whose price already covers this. */
  includedIn: string[];
  /**
   * The manufacturer's video of it, played in its box. Absent on most
   * upgrades, and on every document frozen before 2026-10-01. See
   * `upgradeVideos.ts`.
   */
  video?: UpgradeVideo;
}

export interface UpgradesSection extends PresentedSection {
  id: "upgrades";
  upgrades: PresentedUpgrade[];
}

/**
 * Where the customer chooses, and where the money is added up.
 *
 * The totals are not in here. They cannot be: they depend on the package and
 * the upgrades the customer has picked, which is a thing that happens on their
 * phone, minutes after this document was frozen. What is frozen is the shape of
 * the sum — whether monthly figures are shown at all, and whether a deposit is
 * asked for — and `presentedTotal` and `presentedDeposit` do the arithmetic
 * against the same document.
 */
export interface SummarySection extends PresentedSection {
  id: "summary";
  lead: string;
  /** The label above the monthly figure, or null when payments are off. */
  monthlyNote: string | null;
  /** Whether the deposit block is on this document. */
  deposit: boolean;
  /**
   * The financing terms this document was frozen with, or absent.
   *
   * Absent on every document presented before 2026-09-22 and on any since
   * where nobody typed a rate, a term or a down payment — the monthly figure
   * is then the $13-per-$1,000 illustration, as it always was. Present, it is
   * what the customer's own phone works the payment out from once they have
   * chosen a package (ADR 0201).
   */
  financing?: FinancingTerms | null;
}

/** One panel of the financing section. */
export interface FinancingColumn {
  title: string;
  lines: string[];
}

export interface FinancingSection extends PresentedSection {
  id: "financing";
  columns: FinancingColumn[];
  /** The wide bar under the panels. */
  pill: { title: string; note: string };
  /**
   * The prequalify button, or null when no lender URL is set.
   *
   * Jonathon, 2026-09-21: "there needs to be a button where they can click to
   * get prequalified and it takes them to this link. it's a soft credit pull",
   * and "doesn't need to be a scan to see. that's where the link can be". The
   * URL is `company_settings.financing_application_url` — a lender is a row
   * edit, never a deploy, the same rule ADR 0033 set for the heading. With no
   * URL the panel is left out entirely rather than shown as a button that goes
   * nowhere.
   */
  apply: { title: string; note: string; url: string } | null;
  /**
   * The QR slot this replaced, on 2026-09-21.
   *
   * Only ever read, never written: a document frozen before that day carries
   * one and has to go on rendering as it was signed (ADR 0032).
   */
  qr?: { title: string; note: string; url: string | null };
  /**
   * The payment this job actually works out to, when a rate and a term were
   * typed on the estimate.
   *
   * The panels above sell financing in general — "1–20 Year Terms",
   * "$2K–$100K Loans" — and a customer still has to work out what that means
   * for their roof. Jonathon, 2026-09-22: "we also need to be able to enter in
   * the term and rate so it can show customer their payment." This is that
   * figure, with the arithmetic beside it so it can be checked, and it is
   * absent whenever nobody typed terms.
   */
  quote?: {
    heading: string;
    /**
     * "$142.90/month" when the proposal offers one option, null when it offers
     * several.
     *
     * With four packages on the table there is no single payment to print here
     * — each one carries its own beside its price, and the Summary works out
     * the one they actually chose. Printing the cheapest would be the number
     * they remember and not the number they signed.
     */
    monthly: string | null;
    /** "$2,000.00 down", "$10,000.00 financed", "120 months at 11.9% APR". */
    lines: string[];
    note: string;
  } | null;
  /**
   * The payment calculator the customer works themselves.
   *
   * Jonathon, 2026-09-22: "make a payment slider that the customer can
   * interact with on the presentation page when financing is selected … term
   * and rate varies by credit and is just an estimate. this can live right on
   * top of the financing card."
   *
   * The programmes and the floor are frozen here; the payment is not, because
   * it depends on the package and the upgrades they pick after this document
   * was made. Absent on any document presented before 2026-09-22, which is
   * what leaves those rendering exactly as they were signed (ADR 0032).
   */
  calculator?: {
    heading: string;
    lead: string;
    programs: FinancingProgram[];
    /** The programme the page opens on — the rep's rate when it matches one. */
    defaultProgramKey: string;
    /** The rep's term when the opening programme offers it. */
    defaultMonths: number | null;
    /**
     * Cents. The smallest loan anybody will write.
     *
     * Checked twice: against the job, which decides whether the calculator
     * says anything at all, and against what is left after a down payment,
     * because putting $9,000 down on a $10,000 roof leaves a loan no lender
     * writes either.
     */
    minTotal: Cents;
    /** The largest amount this lender advertises it will finance. */
    maxTotal: Cents;
    /**
     * Where the down payment box starts — whatever the rep put on the
     * estimate, or nothing.
     *
     * The customer types over it. Jonathon, 2026-09-22: "let the customer
     * enter in a downpayment." What they type is theirs and goes nowhere: it
     * is not saved, not sent, and does not change the price they sign for, the
     * deposit or the payment schedule (ADR 0202).
     */
    downPayment: Cents;
    /** The line under the figure, in Jonathon's own words. */
    note: string;
  } | null;
  disclaimers: string[];
}

/**
 * One colour card.
 *
 * A card per package, drawn to the colour sheet Jonathon worked up: the package
 * across the top, the product it is made of under it, and one "View colors"
 * link. `url` is the manufacturer's own colour page when we hold one; when we
 * do not, it is null and the card opens the chart of photographs further down
 * instead. Either way the customer taps a card, not a line of text.
 */
export interface ColorLink {
  key: string;
  /** What the customer taps: "Peace of Mind", "Gutters". */
  name: string;
  /** The product under the package name: "GAF Timberline UHDZ". */
  product?: string;
  /** The manufacturer's colour page, or null to open the chart below. */
  url?: string | null;
  /**
   * The colour viewer this card opens over the page, in place of `url`.
   * Absent on every document frozen before 2026-09-28.
   */
  viewer?: ColorViewer;
  /** Carries the same badge the package section does. */
  recommended?: boolean;
}

/**
 * The colours on this roof, for the packages this document actually offers.
 *
 * A card for a package that is not on the estimate is a card for a roof nobody
 * can buy, so the hub is built from the packages rather than from a list of
 * every shingle we sell.
 */
export interface ColorsSection extends PresentedSection {
  id: "colors";
  links: ColorLink[];
  /** One chart per shingle, keyed to the cards above. */
  shingles: { key: string; shingle: string; colors: ShingleColor[] }[];
  /** The gutter chart, when this document puts gutters on the house. */
  gutters: { key: string; name: string; photos: string[] }[] | null;
  note: string;
}

/**
 * A few of the roofs we have already finished, as one link out to the videos.
 *
 * Jonathon, 2026-09-23: "our estimate presentation should have a page at the
 * bottom where they can see some of our work. it should take them to our
 * youtube page."
 *
 * The last thing on the proposal before the agreement, on purpose. Everything
 * above it is about this house — their photographs, their price, their
 * colours — and the question left over is the one no price answers: whether
 * these people are any good. Proof belongs there, where they have already
 * decided what they want and are deciding who from, and it belongs above the
 * signature rather than below it.
 *
 * A link, never an embed. The videos live on our own channel where they are
 * already published, watched and counted; copying them onto the customer's
 * page would mean re-uploading every one of them and would lose the rest of
 * the channel — which is the point of sending somebody there.
 *
 * `url` is `company_settings.work_gallery_url`, so a new channel, a new
 * playlist or a different gallery altogether is a row edit rather than a
 * deploy — the rule ADR 0033 set for the financing heading. Empty leaves the
 * page out entirely rather than printing a button that goes nowhere.
 *
 * Not on a commercial job. Jonathon, 2026-09-23: "it doesn't show on
 * commercial job types though" — the playlist is houses, and a property
 * manager buying four buildings shown a reel of somebody's shingle roof has
 * been shown work that is not theirs.
 */
export interface WorkSection extends PresentedSection {
  id: "work";
  /** The sentence under the heading. */
  lead: string;
  /** The one thing on the page to tap. */
  link: { title: string; note: string; url: string };
}

export interface ContractSection extends PresentedSection {
  id: "contract";
  version: string;
  /*
   * The four fields below are optional for one reason, and it is not that a
   * document might be built without them — `buildPresentedDocument` always sets
   * all four. It is that a document frozen before 2026-08-16 does not have
   * them, and every one of those is still readable and still signable (ADR
   * 0032). Anything reading a snapshot has to cope with their absence, and a
   * type that promised otherwise would quietly stop it from having to.
   */
  kind?: ContractKind;
  insurance?: boolean;
  companySigner?: string;
  companySignedDate?: string;
  blocks: ContractBlock[];
}

/**
 * A product shown off, in photographs, whenever the document offers it.
 *
 * Today only Pro Flo gutter guards (Jonathon, 2026-10-01: "whenever we select
 * gutter guard upgrade or create an estimate for gutter guards i want this to
 * show on the estimate to the customer so they can see what it looks like").
 * See `productShowcases.ts`, which holds the words and why they are only the
 * manufacturer's.
 *
 * Under the packages and above the upgrades: on a document that offers the
 * guards as an upgrade, the customer reads what they are before the box that
 * adds them; on one that sells them as the job, it follows the price.
 */
export interface ShowcaseSection extends PresentedSection {
  id: "showcase";
  /** The company, then "Upgrade Option" when the guards are an upgrade. */
  eyebrow: string;
  showcase: ProductShowcase;
}

/**
 * The roofs side by side, under the packages and above the upgrades
 * (ADR 0274).
 *
 * Only when the document offers two or more whole roofs that are rivals: the
 * same packages that carry the Key Differentiator, and not when the rep said
 * the roofs are for different buildings (`roofsTogether`). Built from the
 * packages as frozen above it, so it says nothing they do not. See
 * `packageComparison.ts`. Absent on every document frozen before 2026-10-07.
 */
export interface CompareSection extends PresentedSection {
  id: "compare";
  packages: ComparedPackage[];
  /** Print the Class 4 insurance note under the chart. */
  class4Note: boolean;
}

export type DocumentSection =
  | CoverSection
  | FindingsSection
  | VideosSection
  | ScopeSection
  | PackagesSection
  | CompareSection
  | ShowcaseSection
  | UpgradesSection
  | SummarySection
  | FinancingSection
  | ColorsSection
  | WorkSection
  | ContractSection;

export interface PresentedDocument {
  /** Bumped every time a voided document is replaced. Starts at 1. */
  revision: number;
  /** The date on the cover, and the date the company signature carries. */
  date: string;
  contractVersion: string;
  sections: DocumentSection[];
}

/** What the company says about itself, from `company_settings`. */
export interface CompanyFacts {
  name?: string | null | undefined;
  address?: string | null | undefined;
  email?: string | null | undefined;
  phone?: string | null | undefined;
  /*
   * No licence number. It is not a fact the customer's copy carries any more,
   * so it does not travel this far — see `CoverSection.licenseNumber`. It is
   * still on `company_settings` for the licence document itself.
   */
  /** "GAF Certified Contractor" and the like, one per line. */
  certifications?: string[] | null | undefined;
  /** "4.9 stars from 300+ reviews" — free text, because the source varies. */
  reviews?: string | null | undefined;
  insurance?: string | null | undefined;
  /** The financing block's heading. Null takes the built-in default. */
  financingHeading?: string | null | undefined;
  /**
   * Where "Get Prequalified" sends the customer — the lender's own soft-pull
   * application. Empty leaves the button off the document altogether.
   */
  financingApplicationUrl?: string | null | undefined;
  /**
   * Where "See our work" sends the customer — our own YouTube channel of
   * finished jobs. Empty leaves that page off the proposal. See `WorkSection`.
   */
  workGalleryUrl?: string | null | undefined;
  /**
   * `company_settings.financing_programs`, as stored.
   *
   * Read through `financingPrograms`, which drops anything unusable and falls
   * back to the list in `financing.ts` when the row leaves nothing — a
   * financing page with no programmes is worse than one quoting the built-in
   * list (ADR 0202).
   */
  financingPrograms?: unknown | undefined;
}

/**
 * Which sections the rep left on.
 *
 * A section turned off is left out of the document entirely, so this is read
 * once, here, and never travels with the customer's copy. The cover photo
 * defaults to off because there is not always one: it is uploaded per estimate,
 * and the cover reads better with no photograph than with a grey box.
 *
 * "Meet your team" was here and is not any more (Jonathon, 2026-08-17: "remove
 * meet your team for now") — half the team have no headshot yet. It comes back
 * as a section, not a flag, when the photographs do.
 */
export interface SectionToggles {
  coverPhoto?: boolean;
  inspectionPhotos?: boolean;
  /** The walkthroughs under the photographs. See `VideosSection`. */
  productVideos?: boolean;
  financing?: boolean;
  /** The monthly figures, the "payments as low as" line and the deposit block. */
  payments?: boolean;
  colors?: boolean;
  /**
   * The roofs side by side (`CompareSection`). On unless the rep switches it
   * off for this send (Jonathon, 2026-10-07, ADR 0274).
   */
  compare?: boolean;
  /*
   * The three pages that read the same on every proposal: "How Your Roof Goes
   * On", the gutter guard slides and See Our Work. On unless the rep switches
   * one off for this send. Jonathon, 2026-10-20: anything in writing outside
   * the contract has to be removable on the presenting screen (ADR 0294).
   * The walkthrough videos already had their switch, `productVideos`.
   */
  scope?: boolean;
  showcase?: boolean;
  work?: boolean;
}

const TOGGLE_DEFAULTS: Required<SectionToggles> = {
  coverPhoto: false,
  inspectionPhotos: true,
  productVideos: true,
  financing: true,
  payments: true,
  colors: true,
  compare: true,
  scope: true,
  showcase: true,
  work: true,
};

/**
 * One package, as the rep's screen hands it over.
 *
 * The same shape whether the document offers one or five: a package is an
 * estimate, with its own total, its own roof and its own words.
 */
export interface PackageInput extends QuotedRoof {
  /** The `estimates` row. Must be one the proposal offers as an option. */
  estimateId: string;
  name: string;
  /**
   * The Key Differentiator, as the estimate holds it.
   *
   * Pass it whatever the package is. Whether it prints is decided here, by
   * `keyDifferentiatorApplies`, not by the caller — see there for the rule.
   */
  headline?: string | null;
  /**
   * What this package sells, as `contractKindFor` answers it for this one
   * estimate: its own trade and its line items' trades, then the job's bid
   * type. The rep's screen passes it because only the rep's screen has loaded
   * every option's line items.
   *
   * Absent, the package's own `trade` answers, then `bidType` — the same
   * question asked with less to go on. See `packageKind`.
   */
  kind?: ContractKind;
  /**
   * The trades on this estimate's line items, for when its own `trade` is
   * blank. An estimate started from scratch with "Roofing Replacement" lines
   * is a roof option (`isWholeRoofOption`) — a rival of the other roofs, and a
   * carrier of the Key Differentiator. Passed by the rep's screen, which has
   * every option's lines loaded; not frozen into the document.
   */
  itemTrades?: readonly (string | null | undefined)[];
  total: Cents;
  payments?: readonly StoredPayment[];
  /** The estimate's in-house payment plan, where it has one (ADR 0286). */
  plan?: PlanTerms | null;
  /** "What You Get", "Warranty", "Worry Free Wood Bonus". */
  blocks?: PackageBlock[];
  recommended?: boolean;
  /**
   * What the rep changed in this roof's column on the comparison chart, on
   * the preview — `estimates.compare_edits` (ADR 0318). Null or absent: the
   * column fills itself in from the package (ADR 0274).
   */
  compareEdits?: ChartEdits | null;
}

/** One upgrade as the rep's screen hands it over, already priced. */
export interface UpgradeInput {
  key: string;
  name: string;
  blurb?: string | null;
  price: Cents;
  /** Estimate ids whose price already covers it. */
  includedIn?: readonly string[];
}

export interface PresentationInput {
  revision?: number;
  /** ISO date. Passed in rather than read from a clock, so this stays pure. */
  date: string;
  customerName: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  propertyAddress: string | null;
  estimateNumber: string;
  repName?: string | null | undefined;
  repEmail?: string | null;
  /**
   * The rep's own mobile, `profiles.phone`.
   *
   * Printed on the cover in place of the company's, the same rule the payment
   * letters follow (ADR 0097) — a customer given a number to ring should get
   * the person who stood in their driveway. Null for some people, and for a job
   * with no rep, and the company number is what prints then.
   */
  repPhone?: string | null;
  /** Uploaded per estimate. Shown only when `show.coverPhoto` is on. */
  coverPhotoUrl?: string | null;
  inspectionPhotos?: DocumentPhoto[];
  /**
   * The walkthroughs for whatever this estimate is selling, already matched to
   * its products by the caller. Public URLs — see `ProductVideo`.
   */
  productVideos?: readonly ProductVideo[];
  /** The packages this document offers, in the order they should be read. */
  packages: readonly PackageInput[];
  /**
   * The same packages' estimate ids in the order that decides which bought one
   * is the main estimate — `mainEstimateOrder`, never the reading order.
   * Frozen as `PackagesSection.leadOrder`. Left out, the reading order
   * decides, as it did before options could be sorted.
   */
  leadOrder?: readonly string[];
  /**
   * Whether the customer may buy more than one of them — `jobs.multi_option`.
   *
   * The rep ticks "Customer can buy more than one of these" on the estimate
   * card, and until 2026-09-09 the customer's page ignored it entirely: the
   * packages were a radio group whatever the job said, so a customer who had
   * been offered two things to buy could sign for exactly one. Katie Harding
   * ran into it. See ADR 0120.
   */
  multiOption?: boolean;
  /**
   * Whether a person set that box by hand — `jobs.multi_option_manual`.
   *
   * Set by hand and on, the customer may buy more than one whole roof: the
   * rep is saying the roofs are for different buildings (ADR 0124). The
   * default never says that, however it is set.
   */
  multiOptionManual?: boolean;
  /** The upgrades under them, offered once, in the order they should read. */
  upgrades?: readonly UpgradeInput[];
  company: CompanyFacts;
  /** For the agreement's own merge fields. */
  contract: ContractValues;
  /**
   * The job's bid type — "repair", "retail" or "insurance".
   *
   * It decides two separate things and they are worth keeping apart: whether
   * the insurance paragraphs are on the agreement (insurance bids only), and,
   * as a last resort when nothing on the estimate carries a trade, which
   * agreement this is at all.
   */
  bidType: string | null;
  /**
   * Every trade the document sells, across every package it offers. One
   * replacement anywhere makes the whole document a replacement — a packet is
   * one agreement, not four.
   */
  trades: readonly (string | null | undefined)[];
  /** Which sections the rep left on. See `SectionToggles`. */
  show?: SectionToggles;
  /**
   * The colour cards the rep switched off, as `ColorCard.key` values.
   *
   * Jonathon, 2026-09-18: the switch is per card, not per page — "we can keep
   * roof replacements on but gutters off". A card left out is absent from the
   * frozen document; with every card off, so is the page. See `colorCards`.
   */
  hiddenColors?: readonly string[] | null | undefined;
  /**
   * The job's type — "residential" or "commercial".
   *
   * The document's own words depend on it. Jonathon, 2026-08-27: "commercial
   * presentations shouldn't mention things like homeowners, your home, etc."
   * See `commercialWording.ts`; absent, the copy reads as it was written.
   */
  jobType?: string | null;
  /**
   * The financing terms for this job: a down payment, a term and a rate.
   *
   * Whatever the rep typed on the estimate, with the company's own defaults
   * already filled in by the caller — this file does not know what a default
   * is, only what this document says. Absent or empty leaves every monthly
   * figure the illustration it was before 2026-09-22 (ADR 0201).
   */
  financing?: FinancingTerms | null;
}

/**
 * The financing panels, in the owner's words.
 *
 * Verbatim from the proposal he has been handing out; the only thing computed
 * anywhere near financing is the monthly figure beside a price.
 *
 * One panel, not two. The Secured / HELOC panel came off on 2026-09-21
 * ("take off the secured/heloc section") along with the terms it quoted, and
 * the unsecured terms were corrected the same day: "anywhere from 1-20 Year
 * Terms. 2k to 100k." Documents already presented keep the panel they were
 * shown with — they are frozen (ADR 0032).
 */
export const FINANCING_COLUMNS: readonly FinancingColumn[] = [
  {
    title: "Unsecured Lending",
    lines: [
      "Easy Apply — Soft Credit Approval",
      "1–20 Year Terms",
      "$2K–$100K Loans",
      "No Home Equity Required",
      "Low Monthly Payments",
      "No Prepay Penalty",
      "100% Financing Available",
    ],
  },
];

/**
 * What the "See Our Work" page says. See `WorkSection` for why it is a page.
 *
 * Short on purpose: this far down a proposal the customer is not reading
 * another paragraph, and the videos are the argument.
 */
/**
 * A link we are willing to put in front of a customer, or nothing.
 *
 * https only, and parsed rather than pattern-matched: these URLs are typed into
 * Tools by hand, and a half-typed one has to leave the card off the document
 * rather than print a button that goes nowhere or, worse, somewhere else.
 */
function outboundUrl(value: string | null | undefined): string {
  try {
    const parsed = new URL((value ?? "").trim());
    return parsed.protocol === "https:" ? parsed.href : "";
  } catch {
    return "";
  }
}

export const WORK_HEADING = "See Our Work";
export const WORK_LEAD =
  "Roofs, siding and repairs we have already finished for neighbours near you. " +
  "Have a look at a few of them before you decide.";
export const WORK_LINK_TITLE = "Watch our work";
export const WORK_LINK_NOTE = "Opens our video channel in a new tab";

export const SUMMARY_LEAD =
  "Please select one option from this proposal, then select any associated upgrades, and sign it with any notes.";

/**
 * The monthly figure beside a price.
 *
 * Every $1,000 is about $13 a month, and the floor that stops a small job
 * reading "$26/month" is in `financing.ts` along with the reason for it.
 *
 * "Approximately", not "about". Jonathon, 2026-08-19 — it is a figure beside a
 * contract price and it should read like one.
 */
function monthlyLabel(
  total: Cents,
  terms?: FinancingTerms | null,
): string | null {
  const payment = financingPayment(total, terms);
  if (payment == null) return null;
  /*
   * "Approximately" belongs to the illustration and goes with it. A payment
   * worked out from a rate and a term the rep typed is not approximate — it is
   * what that loan costs — and hedging it would make the exact figure read as
   * another guess.
   */
  return payment.quoted
    ? `or ${payment.formatted}/mo`
    : `or approximately ${payment.formatted}/mo`;
}

/**
 * What an upgrade adds a month, in the same two voices.
 *
 * "Approximately", spelled out, is the wording Jonathon asked for on 2026-08-19
 * and it stays on the illustration. With terms typed the figure is exact and
 * says so.
 */
function upgradeMonthlyLabel(
  price: Cents,
  terms?: FinancingTerms | null,
): string | null {
  const addition = financingAddition(price, terms);
  if (addition == null) return null;
  return addition.quoted
    ? `${addition.formatted} more a month`
    : `approximately ${addition.formatted} more a month`;
}

/**
 * The terms, if there is anything in them worth freezing into a document.
 *
 * A row of nulls is every estimate saved before 2026-09-22 and most of them
 * since, and carrying `{ downPayment: null, termMonths: null, apr: null }`
 * into a snapshot would make `SummarySection.financing` present-but-empty on
 * documents that have no terms at all — a difference no reader could act on
 * and every test would have to know about.
 */
/**
 * The terms panel on the financing page.
 *
 * Returns null when the terms amount to a down payment and nothing else: a
 * panel reading "$2,000 down" with no rate and no term explains nothing the
 * price beside each option has not already said, and every figure on the
 * document is still the illustration.
 */
function financingQuote(
  terms: FinancingTerms,
  packages: readonly PackageInput[],
): FinancingSection["quote"] {
  if (terms.termMonths == null || terms.apr == null) return null;

  const sole = packages.length === 1 ? packages[0] : undefined;
  const payment = sole ? financingPayment(sole.total, terms) : null;
  const down = Number(terms.downPayment ?? 0);

  return {
    heading: "Your Financing",
    monthly: payment && payment.quoted ? `${payment.formatted}/month` : null,
    lines: [
      ...(down > 0 ? [`${formatMoney(down as Cents)} down`] : []),
      ...(payment && down > 0
        ? [`${formatMoney(payment.financed)} financed`]
        : []),
      formatTerm(terms.termMonths),
      `${formatApr(terms.apr)} APR`,
    ],
    note:
      packages.length === 1
        ? "First payment 30 days after we finish."
        : "The monthly figure beside each option is worked out from these.",
  };
}

/**
 * The calculator that sits on top of the financing card.
 *
 * Never null in practice — `financingPrograms` falls back to the built-in
 * list — but typed as though it could be, so a future company row that
 * deliberately offers nothing leaves the page as it was before 2026-09-22
 * rather than showing an empty widget.
 *
 * **A typed rate or term replaces the plans with one** (ADR 0250). Jonathon,
 * 2026-10-01: "if we manually put an interest rate or term it shouldn't show
 * the customer an option of the 0, 10.9, or 11.9 etc. that would confuse
 * them." So the customer is offered the rate the rep quoted — or, with only a
 * term typed, the rate the page would have opened on — at the term the rep
 * quoted, or across that rate's own range when no term was typed.
 */
function financingCalculator(
  storedPrograms: unknown,
  terms: FinancingTerms | null,
): FinancingSection["calculator"] {
  const programs = [...financingPrograms(storedPrograms)];
  const opening = defaultFinancingProgram(programs, terms?.apr ?? null);
  if (!opening) return null;
  const common = {
    heading: "What would that be a month?",
    minTotal: FINANCING_MIN_TOTAL,
    maxTotal: FINANCING_MAX_TOTAL,
    downPayment: (Number(terms?.downPayment ?? 0) || 0) as Cents,
    // Jonathon's own words, 2026-09-22: "term and rate varies by credit and is
    // just an estimate." It is the whole reason a customer may be shown a
    // figure at all before anybody has underwritten them.
    note: "Your term and rate depend on your credit — this is an estimate, not an offer.",
  };

  if (terms?.apr != null || terms?.termMonths != null) {
    const quoted = quotedProgram(opening, terms);
    const fixed = quoted.minMonths === quoted.maxMonths;
    return {
      ...common,
      lead: fixed
        ? "Your payment at the terms we quoted. Nothing here affects your price."
        : "Move the slider to change the length of the loan. Nothing here affects your price.",
      programs: [quoted],
      defaultProgramKey: quoted.key,
      defaultMonths: quoted.defaultMonths,
    };
  }

  return {
    ...common,
    lead: "Pick a plan and move the slider. Nothing here affects your price.",
    programs,
    defaultProgramKey: opening.key,
    defaultMonths: null,
  };
}

/**
 * The one plan a rep's typed terms make: their rate (or the opening plan's),
 * at their term (or across the opening plan's range).
 */
function quotedProgram(
  opening: FinancingProgram,
  terms: FinancingTerms,
): FinancingProgram {
  const apr = terms.apr ?? opening.apr;
  const months = terms.termMonths;
  return {
    key: "quoted",
    label: `${formatApr(apr)} APR`,
    apr,
    ...(months != null
      ? {
          minMonths: months,
          maxMonths: months,
          defaultMonths: months,
          note: formatTerm(months),
        }
      : {
          minMonths: opening.minMonths,
          maxMonths: opening.maxMonths,
          defaultMonths: opening.defaultMonths,
          ...(opening.note ? { note: opening.note } : {}),
        }),
  };
}

function usableTerms(
  terms: FinancingTerms | null | undefined,
): FinancingTerms | null {
  if (!terms) return null;
  const downPayment = Number.isFinite(terms.downPayment)
    ? Number(terms.downPayment)
    : 0;
  const termMonths = Number.isFinite(terms.termMonths)
    ? Number(terms.termMonths)
    : null;
  const apr = Number.isFinite(terms.apr) ? Number(terms.apr) : null;
  if (downPayment <= 0 && termMonths == null && apr == null) return null;
  return {
    ...(downPayment > 0 ? { downPayment: downPayment as Cents } : {}),
    ...(termMonths != null ? { termMonths } : {}),
    ...(apr != null ? { apr } : {}),
  };
}

/**
 * Whether a package sells a repair or a replacement.
 *
 * Its own `kind` when the caller worked one out, otherwise `contractKindFor`
 * asked of the package's own trade, falling back to the job's bid type. Never
 * a second notion of "replacement": this is the question that chooses the
 * agreement, asked of one package (ADR 0037).
 *
 * It no longer decides the Key Differentiator. A siding replacement is a
 * replacement and is not a roof option (ADR 0124) — see
 * `keyDifferentiatorApplies`.
 */
export function packageKind(
  pkg: Pick<PackageInput, "kind" | "trade">,
  bidType: string | null | undefined,
): ContractKind {
  return pkg.kind ?? contractKindFor({ bidType, trades: [pkg.trade] });
}

/**
 * Whether a package carries the Key Differentiator, given what is beside it.
 *
 * Jonathon, 2026-09-10: "It should print on roof options only." The line
 * tells a customer why THIS roof rather than the one beside it — "30+ Year
 * Life Expectancy" — so it needs two or more whole roofs on the document,
 * and only the whole roofs carry it. Gutters, siding and a deck never do,
 * whatever else is offered beside them.
 *
 * "A whole roof" is `isWholeRoofOption`: the package's own trade, or its line
 * items' trades when that is blank. The same test freezes `wholeRoof` and makes
 * two roofs rivals, so the packages the line argues between are exactly the
 * packages the customer chooses between. It already keeps repairs out
 * (2026-09-07: "shouldn't appear on a repair estimate presenting screen") and
 * needs two (2026-09-08: "only for when you're pitching multiple replacement
 * estimates").
 *
 * An estimate with no trade whose lines are "Roofing Replacement" is a roof
 * option here, as #195 made it; for a few hours on 2026-09-10 it was not.
 *
 * It no longer cares whether the document sells several things. Until
 * 2026-09-10 a `chooseMany` document printed it nowhere; two roofs and the
 * gutters now print it on the two roofs, which are still a choice (ADR 0124).
 *
 * `offered` is every package on the document, this one included.
 */
export function keyDifferentiatorApplies(
  pkg: RoofOption,
  offered: readonly RoofOption[],
): boolean {
  if (!isWholeRoofOption(pkg)) return false;
  return offered.filter((each) => isWholeRoofOption(each)).length >= 2;
}

/**
 * The estimates whose package prints its Key Differentiator.
 *
 * The one place the rule is applied to a list of packages. The document is
 * built through it, and the rep's screen asks it whether to offer the box, so
 * the box and the customer's copy cannot disagree about which packages count
 * as roof options.
 */
export function keyDifferentiatorPackages(
  packages: readonly Pick<
    PackageInput,
    "estimateId" | "trade" | "itemTrades"
  >[],
): string[] {
  return packages
    .filter((pkg) => keyDifferentiatorApplies(pkg, packages))
    .map((pkg) => pkg.estimateId);
}

/**
 * Every package, priced and dressed for comparison.
 *
 * `recommended` is honoured on **at most one** package, the first that claims
 * it. Two recommendations is not a recommendation, and the alternative — a
 * runtime throw, deep inside building a document a rep is trying to send — is a
 * worse answer to a mistake this cheap to absorb.
 */
function buildPackages(
  inputs: readonly PackageInput[],
  payments: boolean,
  jobType: string | null | undefined,
  terms?: FinancingTerms | null,
): PresentedPackage[] {
  let recommended = false;
  const differentiated = new Set(keyDifferentiatorPackages(inputs));
  return inputs.map((pkg) => {
    const isRecommended = Boolean(pkg.recommended) && !recommended;
    if (isRecommended) recommended = true;
    const typed = typedFirstPayment(pkg.payments ?? []);
    /*
     * A new construction roof is one template priced with whichever shingle
     * the builder wants, so it carries no Key Differentiator of its own: with
     * none typed, it reads its shingle's — "40+ Year Life Expectancy. 15 Year
     * Warranty" on UHDZ — and its warranty block says the same years
     * (ADR 0318). A line the rep typed on the preview is kept whatever the
     * shingle. Every other roof is one template per shingle and says its own,
     * and a labor only one keeps the template's 5 year workmanship words
     * (`packageShingleTerms`).
     */
    const shingleTerms = packageShingleTerms(pkg);
    const headline = pkg.headline?.trim() || packageShingleDifferentiator(pkg);
    const years = (text: string | null | undefined) =>
      shingleTerms && text
        ? withShingleWarrantyYears(text, shingleTerms)
        : text;
    return {
      estimateId: pkg.estimateId,
      name: pkg.name,
      headline: differentiated.has(pkg.estimateId)
        ? wordingForOptional(jobType, headline)
        : null,
      total: pkg.total,
      totalFormatted: formatMoney(pkg.total),
      monthly: payments ? monthlyLabel(pkg.total, terms) : null,
      /*
       * Reworded here as well as in `packageBlocks`, which is where the rep's
       * screen builds these. Twice is free — the substitution changes nothing
       * the second time — and it means the document reads correctly whoever
       * assembled the blocks, including a caller that never went through the
       * Present screen.
       */
      blocks: (pkg.blocks ?? []).map((block) => ({
        heading: wordingFor(jobType, block.heading),
        lines: block.lines.map((line) => ({
          ...line,
          name: wordingFor(jobType, years(line.name) ?? line.name),
          body: wordingForOptional(jobType, years(line.body)),
        })),
      })),
      recommended: isRecommended,
      ...(isWholeRoofOption(pkg) ? { wholeRoof: true } : {}),
      // Always written, null included: absent means a document frozen before
      // this, and signing reads that differently (`typedFirstPaymentAtSigning`).
      typedFirstPayment: typed,
      schedule: frozenPayments(pkg.payments ?? []),
      ...(pkg.plan ? { plan: pkg.plan } : {}),
    };
  });
}

/**
 * One colour card the document could carry, for a screen that offers to
 * switch it off.
 *
 * The same cards `buildColors` draws, before `hiddenColors` is applied: a
 * switch that vanished when you turned it off could never be turned back on.
 * `key` is stable — the shingle line's key, or "gutters" — and is what
 * `estimates.hidden_colors` stores; `name` is the package it belongs to and
 * `product` the roof or gutter it is the colours of.
 */
export interface ColorCard {
  key: string;
  name: string;
  product: string;
}

/** What `colorCards` needs of a package: its roof, and the lines that name it. */
export interface ColorCardPackage {
  name: string;
  roofType?: string | null;
  /** The names on the package's blocks — where a shingle names itself. */
  lineNames: readonly string[];
}

export const GUTTER_COLOR_CARD: ColorCard = {
  key: "gutters",
  name: "Gutters",
  product: 'Seamless 6" Gutters',
};

/**
 * The siding card, for the siding product the document offers.
 *
 * Siding is looked for on every line offered, as gutters are, because it is
 * never what a package's roof is made of. See `sidingColors.ts`.
 */
const sidingCard = (line: SidingLine): ColorCard => ({
  key: line.key,
  name: "Siding",
  product: line.product,
});

/**
 * The colour cards a document with these packages would carry.
 *
 * One per shingle or panel lineup the packages name — two packages of the same
 * shingle share a card — then the gutters when anything offered puts gutters
 * on the house, then the siding when anything offered is a siding product we
 * hold a lineup for (`sidingColors.ts`). Bare Bones has no lineup on purpose and produces no card; see
 * `shingleColors.ts`.
 */
export function colorCards(
  packages: readonly ColorCardPackage[],
  offeredNames: readonly string[],
): ColorCard[] {
  const cards: ColorCard[] = [];
  for (const pkg of packages) {
    /*
     * The roof type first: a metal package names its panel there — "Standing
     * Seam Metal", "AG Panel 26 Gauge Residential" — where a shingle package
     * only says "Shingle" and falls through. Then the package's own lines,
     * which is where a shingle names itself: the headline line reads
     * "Shingles | Timberline HDZ".
     *
     * There used to be a third and it was tried first: `shingleType`, off
     * `estimates.shingle_type`. That column was dropped on 2026-08-24 with the
     * boxes that were supposed to fill it (ADR 0056) — and nothing had ever
     * filled it, so it was null on every estimate and this list has in
     * practice always been the two here.
     */
    const line = shingleLineFor([pkg.roofType, ...pkg.lineNames]);
    if (!line) continue;
    if (cards.some((card) => card.key === line.key)) continue;
    cards.push({ key: line.key, name: pkg.name, product: line.shingle });
  }
  if (gutterColorsApply(offeredNames)) cards.push({ ...GUTTER_COLOR_CARD });
  const siding = sidingLineFor(offeredNames);
  if (siding) cards.push(sidingCard(siding));
  return cards;
}

/** The names a package's blocks carry, which is where a shingle names itself. */
const packageLineNames = (pkg: PackageInput): string[] =>
  (pkg.blocks ?? []).flatMap((block) => block.lines.map((entry) => entry.name));

/**
 * The siding card's link, opening its viewer, unless the rep switched it off.
 * A copy of the lineup rather than the lineup itself, because the document is
 * frozen into a snapshot at send (ADR 0032).
 */
function sidingLink(
  offeredNames: readonly string[],
  hidden: ReadonlySet<string>,
): ColorLink | null {
  const siding = sidingLineFor(offeredNames);
  if (!siding || hidden.has(siding.key)) return null;
  const { viewer } = siding;
  return {
    ...sidingCard(siding),
    url: null,
    viewer: {
      ...viewer,
      slides: viewer.slides.map((slide) => ({ ...slide })),
      ...(viewer.grid ? { grid: { ...viewer.grid } } : {}),
    },
    recommended: false,
  };
}

/**
 * The colour hub, built from what this document offers.
 *
 * A link per package whose shingle we hold a lineup for, then the gutters when
 * the document puts gutters on the house — the cards `colorCards` lists, less
 * the ones the rep switched off. Bare Bones has no lineup on purpose and simply
 * produces no link — see `shingleColors.ts`.
 */
function buildColors(
  packages: readonly PackageInput[],
  offeredNames: readonly string[],
  jobType: string | null | undefined,
  hiddenColors: readonly string[],
): ColorsSection | null {
  const hidden = new Set(hiddenColors);
  const links: ColorLink[] = [];
  const shingles: ColorsSection["shingles"] = [];

  for (const pkg of packages) {
    const line = shingleLineFor([pkg.roofType, ...packageLineNames(pkg)]);
    if (!line) continue;
    if (hidden.has(line.key)) continue;
    /*
     * A chart only for a shingle whose colours we hold ourselves. Where the
     * manufacturer publishes the lineup, the card opens that instead, and
     * printing a hundred photographs underneath it as well would put a mile of
     * look-book between the customer and the price. A line with a viewer
     * opens that over the page instead, for the same reason.
     */
    if (
      !line.colorsUrl &&
      !line.viewer &&
      !shingles.some((chart) => chart.key === line.key)
    ) {
      shingles.push({
        key: line.key,
        shingle: line.shingle,
        colors: line.colors.map((color) => ({
          ...color,
          photos: [...color.photos],
        })),
      });
    }
    if (!links.some((link) => link.key === line.key)) {
      links.push({
        key: line.key,
        name: pkg.name,
        product: line.shingle,
        url: line.colorsUrl ?? null,
        ...(line.viewer
          ? {
              viewer: {
                ...line.viewer,
                slides: line.viewer.slides.map((slide) => ({ ...slide })),
              },
            }
          : {}),
        recommended: pkg.recommended === true,
      });
    }
  }

  const gutters =
    gutterColorsApply(offeredNames) && !hidden.has(GUTTER_COLOR_CARD.key)
      ? GUTTER_COLORS.map((color) => ({ ...color, photos: [...color.photos] }))
      : null;
  if (gutters)
    links.push({
      ...GUTTER_COLOR_CARD,
      url: null,
      recommended: false,
    });

  const siding = sidingLink(offeredNames, hidden);
  if (siding) links.push(siding);

  if (links.length === 0) return null;
  return {
    id: "colors",
    heading: "See Your Color Options",
    links,
    shingles,
    gutters,
    note: wordingFor(jobType, SHINGLE_COLOR_NOTE),
  };
}

/** The document, exactly as it will be snapshotted and shown. */
export function buildPresentedDocument(
  input: PresentationInput,
): PresentedDocument {
  const show = { ...TOGGLE_DEFAULTS, ...(input.show ?? {}) };
  /*
   * The monthly figures go with the financing page, always.
   *
   * Jonathon, 2026-08-21: "if we toggle off financing it should also toggle
   * off the monthly payment option." They are one offer — "or about $445/mo"
   * beside a price is the financing page's promise, made early, and leaving it
   * on a proposal that carries no financing page quotes a payment plan the
   * document never explains.
   *
   * The deposit is not part of this. That is what they owe at signing, whether
   * or not anybody finances the rest.
   */
  const monthly = show.payments && show.financing;

  /*
   * The terms the rep typed, or nothing.
   *
   * Nothing is the common case and the safe one: with no rate and no term
   * every figure on the document is the illustration it has always been. A
   * half-filled set — a rate and no term — is `financingPayment`'s problem
   * rather than this one, and it falls back the same way.
   *
   * Dropped entirely when the financing page is off, along with every other
   * monthly figure. Quoting "$142.90/mo at 11.9%" on a proposal that carries
   * no financing page is the same mistake the paragraph above guards against,
   * one decimal place worse.
   */
  const terms = monthly ? usableTerms(input.financing) : null;
  const packages = input.packages ?? [];
  const upgrades = input.upgrades ?? [];

  const cover: CoverSection = {
    id: "cover",
    heading: "Proposal",
    date: input.date,
    customerName: input.customerName ?? "",
    customerEmail: input.customerEmail ?? null,
    customerPhone: input.customerPhone ?? null,
    propertyAddress: input.propertyAddress ?? "",
    estimateNumber: input.estimateNumber,
    repName: input.repName ?? null,
    repEmail: input.repEmail ?? null,
    companyName: input.company.name ?? "",
    /*
     * The number to ring about this proposal — the rep's own if they have one.
     *
     * It sits under the rep's name on the cover, so it was reading as theirs
     * while it was the office line. Jonathon, 2026-09-07: "the phone number can
     * be whomever the rep is ... if there's not a rep it can be lysa and her
     * phone number". The company number already is Lysa's — `company_settings`
     * and her profile both hold 205-830-7127 — so the fallback needs no special
     * case, and it stays right if the office line ever moves to somebody else.
     *
     * Only the cover. The agreement's own `{{company.phone}}` names departments
     * — "our billing department", "our insurance department", "direct them to"
     * — and a rep's mobile printed against those would send an adjuster to a
     * cell phone. Those stay the company's.
     *
     * The field keeps its name because a frozen document (ADR 0032) carries it,
     * and what it has always meant is "the number printed here".
     *
     * Trimmed rather than null-checked, the same test `repName` gets below and
     * the same one the payment letters already use (`paymentEmails.ts`). A
     * profile somebody half-filled in holds an empty string rather than a null,
     * and `??` would print it: a cover with no number on it at all, which is
     * worse than the office line it was meant to replace.
     */
    companyPhone:
      (input.repPhone ?? "").trim() || (input.company.phone ?? null),
    licenseNumber: null,
    coverPhotoUrl: show.coverPhoto ? (input.coverPhotoUrl ?? null) : null,
  };

  const sections: DocumentSection[] = [cover];

  /*
   * A proposal with no photographs is ordinary — a repair quoted off a phone
   * call has none — so an empty section is left out rather than sent as an
   * empty box. The URLs here are storage paths; the Edge Function signs them
   * when it serves the customer's copy.
   */
  const photos = input.inspectionPhotos ?? [];
  if (show.inspectionPhotos && photos.length > 0) {
    sections.push({
      id: "findings",
      // Not "Your Roof Inspection Photos". This section is the one piece of
      // roof-specific wording with no gate on it: the eight-step page is held
      // back unless every quoted roof is a full shingle replacement, and the
      // agreement heading was already made trade-neutral for exactly this
      // reason ("sometimes we aren't selling roof repairs", 2026-08-24). The
      // photographs are shown on any job that has them — a repair, a siding
      // run, an estimate built from scratch — and named the trade anyway.
      // Jonathon, 2026-08-30: "if we select create from scratch or the other
      // repair estimate that's not a replacement it shouldn't say your roof."
      //
      // Naming no trade is right for all of them, and needs no condition.
      heading: "Inspection Photos",
      photos: photos.map((photo) => ({ ...photo })),
    });
  }

  /*
   * Under the photographs, and only when the estimate actually sells something
   * that has one. Most estimates have no video at all and get no section — the
   * same rule the photographs follow, for the same reason: an empty box on a
   * customer's proposal is worse than one section fewer.
   *
   * Deduplicated by URL. A document can offer the same repair in more than one
   * package, and one walkthrough played twice on one page is a mistake, not a
   * feature.
   */
  const videos = dedupeBy(input.productVideos ?? [], (video) => video.url);
  if (show.productVideos && videos.length > 0) {
    sections.push({
      id: "videos",
      // Names no trade and no product, because it heads a list that might be
      // one video or three. The video's own title says which repair it is.
      heading: "See the work",
      videos: videos.map((video) => ({ ...video })),
    });
  }

  /*
   * How we will build it, under the photographs of what is there now. Only on
   * a shingle or TimberSteel replacement: every step describes a tear-off, and
   * on a standing seam or a repair they are promises about work nobody is
   * going to do.
   */
  const roofs: QuotedRoof[] = packages.map((pkg) => ({
    roofType: pkg.roofType,
    trade: pkg.trade,
  }));
  if (show.scope && scopeOfWorkApplies(roofs)) {
    sections.push({
      id: "scope",
      // Its own words rather than "Scope of Work", which now belongs to the
      // block inside each package (Jonathon, 2026-08-19: "instead of what you
      // get it should say scope of work"). Two headings reading the same on one
      // page is how a customer loses their place; this is the eyebrow this
      // section already carried.
      heading: "How Your Roof Goes On",
      steps: SCOPE_OF_WORK_STEPS.map((step) => ({
        ...step,
        heading: wordingFor(input.jobType, step.heading),
        points: step.points.map((point) => wordingFor(input.jobType, point)),
      })),
      closer: {
        bar: wordingFor(input.jobType, SCOPE_OF_WORK_CLOSER.bar),
        line: wordingFor(input.jobType, SCOPE_OF_WORK_CLOSER.line),
      },
    });
  }

  // Only ever true, never false: a document offering one package is not a
  // choice of any kind, and marking it "choose as many as you like" would be a
  // promise about a list of one. The same goes for a list of roofs: the box is
  // the job's, but the document offers only what the rep ticked, and two roof
  // systems on one house are rivals however the job is set (ADR 0120). So it
  // takes two things that could be bought together — unless a person set the
  // box by hand, which is the rep saying the roofs are for different
  // buildings (ADR 0124).
  const roofsTogether = roofsBuyableTogether(
    input.multiOption,
    input.multiOptionManual,
  );
  const chooseMany = documentSellsSeveral(
    input.multiOption,
    packages,
    roofsTogether,
  );

  const presented = buildPackages(packages, monthly, input.jobType, terms);
  // Which bought package is the main one, kept apart from the reading order so
  // sorting the options on the preview cannot move the upgrades or the
  // payment schedule onto another estimate (ADR 0294).
  const offeredIds = new Set(packages.map((pack) => pack.estimateId));
  const leadOrder = (input.leadOrder ?? []).filter((id) => offeredIds.has(id));
  sections.push({
    id: "packages",
    heading: "Your Options",
    packages: presented,
    ...(chooseMany ? { chooseMany: true } : {}),
    ...(chooseMany && roofsTogether ? { roofsTogether: true } : {}),
    ...(chooseMany && leadOrder.length > 0 ? { leadOrder } : {}),
  });

  // The roofs side by side, once they have each been read in full (ADR 0274).
  const compare =
    show.compare &&
    documentComparesRoofs(input.multiOption, input.multiOptionManual, packages)
      ? buildCompare(packages, presented, upgrades, input.jobType)
      : null;
  if (compare) sections.push(compare);

  const showcase = buildShowcase(
    packages,
    upgrades.map((upgrade) => upgrade.name),
    input.company.name ?? "",
  );
  if (show.showcase && showcase) sections.push(showcase);

  // Once, under all of them. Somebody has to have chosen a roof before "add
  // gutters to it" means anything (Jonathon, 2026-08-15), and repeating the
  // same four upgrades under five packages is the same list four times over.
  if (upgrades.length > 0) {
    sections.push({
      id: "upgrades",
      heading: "Upgrades",
      upgrades: upgrades.map((upgrade) => ({
        key: upgrade.key,
        name: wordingFor(input.jobType, upgrade.name),
        blurb: wordingForOptional(input.jobType, upgrade.blurb),
        price: upgrade.price,
        priceFormatted: formatMoney(upgrade.price),
        monthly: monthly ? upgradeMonthlyLabel(upgrade.price, terms) : null,
        includedIn: [...(upgrade.includedIn ?? [])],
        ...upgradeVideoField(upgrade.name),
      })),
    });
  }

  if (show.financing) {
    // An unset or invalid lender URL leaves the button off the page.
    const applyUrl = outboundUrl(input.company.financingApplicationUrl);
    /*
     * The terms, spelled out, above the panels that sell financing in general.
     *
     * A payment only when the proposal offers ONE option — see `quote.monthly`
     * for why. The lines are always the terms themselves, because a customer
     * reading "$142.90/mo" beside a price is owed the arithmetic that produced
     * it somewhere on the page.
     */
    const quote = terms ? financingQuote(terms, packages) : null;
    /*
     * The calculator, above everything else on the page.
     *
     * It is frozen with the programmes and the floor but NOT with a payment:
     * the customer chooses a package and ticks upgrades after this document is
     * made, and the figure has to follow what they chose. The rep's own rate
     * decides which programme it opens on, so the page does not quote the same
     * roof two ways.
     */
    const calculator = financingCalculator(
      input.company.financingPrograms,
      terms,
    );
    sections.push({
      id: "financing",
      heading: "Easy Financing",
      columns: FINANCING_COLUMNS.map((column) => ({
        title: column.title,
        lines: column.lines.map((line) => wordingFor(input.jobType, line)),
      })),
      pill: {
        title: "First Payment in 30 Days",
        note: "Start your project now — pay later",
      },
      apply: applyUrl
        ? {
            title: "Get Prequalified",
            note: "Soft credit pull — won't affect your score",
            url: applyUrl,
          }
        : null,
      ...(quote ? { quote } : {}),
      ...(calculator ? { calculator } : {}),
      disclaimers: quote
        ? [
            /*
             * One line more than an illustrated document carries, and it is
             * the one that keeps the figure honest: the rate on this page is
             * the programme we expect them to be offered, not the one the
             * lender has written for them. Underwriting decides.
             */
            "Estimated payment — your rate and term are set by the lender",
            "Subject to credit approval",
            "Soft credit pull only",
          ]
        : ["Subject to credit approval", "Soft credit pull only"],
    });
  }

  if (show.colors) {
    const offeredNames = [
      ...packages.flatMap((pkg) =>
        (pkg.blocks ?? []).flatMap((block) =>
          block.lines.map((line) => line.name),
        ),
      ),
      ...upgrades.map((upgrade) => upgrade.name),
    ];
    const colors = buildColors(
      packages,
      offeredNames,
      input.jobType,
      input.hiddenColors ?? [],
    );
    if (colors) sections.push(colors);
  }

  /*
   * Our own finished work, last of the pages that sell — after the colours and
   * immediately before the ask.
   *
   * Left off a commercial proposal, and left off entirely when nobody has put
   * a link in Tools. See `WorkSection` for both reasons.
   */
  if (show.work && !isCommercialJob(input.jobType)) {
    const workUrl = outboundUrl(input.company.workGalleryUrl);
    if (workUrl) {
      sections.push({
        id: "work",
        heading: WORK_HEADING,
        lead: WORK_LEAD,
        link: { title: WORK_LINK_TITLE, note: WORK_LINK_NOTE, url: workUrl },
      });
    }
  }

  /*
   * The ask, last — after the money and the colours, immediately before the
   * agreement it is asking them to sign.
   *
   * It used to sit directly under the packages, which put "choose and sign"
   * ahead of both the monthly payment and the colour charts. So the document
   * asked for a decision, and only then answered the objection that decision
   * runs into ("that is a lot of money" — the financing page) and offered the
   * part a customer actually enjoys (picking a colour). A rep works in that
   * order for a reason; the document now does too.
   *
   * It also makes the section honest. Its own lead says "then sign below" and
   * the signature was three sections further on; from here, below is where the
   * signature is.
   *
   * Already-sent documents are untouched: `sections` is frozen into
   * `proposals.snapshot` at send and the customer's page renders that stored
   * array in its stored order, so this only changes documents presented from
   * now on.
   */
  sections.push({
    id: "summary",
    heading: "Summary",
    lead: SUMMARY_LEAD,
    monthlyNote: monthly
      ? terms?.termMonths != null && terms.apr != null
        ? "Your payment"
        : "Payments as low as"
      : null,
    deposit: show.payments,
    /*
     * The terms travel with the frozen document, because the figure beside
     * them does not exist yet.
     *
     * The customer picks a package and ticks upgrades minutes after this was
     * frozen, so the monthly payment on the Summary is worked out on their
     * phone from whatever they chose. That arithmetic needs the rate, the term
     * and the down payment, and the only place it can read them is here — a
     * document presented before 2026-09-22 carries none, and falls back to the
     * $13-per-$1,000 illustration exactly as it always did.
     */
    ...(monthly && terms ? { financing: terms } : {}),
  });

  const kind = contractKindFor({
    bidType: input.bidType,
    trades: input.trades,
  });
  const insurance = showsInsuranceTerms(input.bidType, kind);
  const warranty = warrantySectionFor(input.trades);
  /*
   * A new construction roof sold with its materials carries its shingle's
   * workmanship years, and the agreement signed with it says so beside the
   * five it gives everything else (ADR 0318). Labor only and Titan XT stay at
   * five, so they add nothing here.
   */
  const longerWorkmanship: LongerWorkmanship[] =
    kind === "replacement" && warranty
      ? packages.flatMap((pkg) => {
          const terms = packageShingleTerms(pkg);
          return terms && terms.warrantyYears > 5
            ? [{ roof: terms.name, years: terms.warrantyYears }]
            : [];
        })
      : [];
  const version = contractVersionFor(
    input.jobType,
    input.trades,
    longerWorkmanship,
  );
  sections.push({
    id: "contract",
    // Deliberately not "Roofing Agreement". Jonathon, 2026-08-24: "sometimes
    // we aren't selling roof repairs", and a siding job, a deck or a gutter
    // run signs the same document — a heading naming the wrong trade is the
    // first line the customer reads. The trade is already said on the packages
    // above; this heading only has to say what the page is.
    heading:
      kind === "repair"
        ? "Repair Agreement & Payment Terms"
        : "Agreement & Payment Terms",
    version,
    kind,
    insurance,
    /*
     * The rep who sold it signs it, and the office signs for a job with no rep.
     *
     * Jonathon, 2026-09-07: "the company signature can now be whoever the sales
     * rep is. doesn't have to be lysa ... if there's not a rep it can be lysa
     * and her phone number." Frozen at send like everything else here, so a
     * contract already signed keeps whoever signed it — including every one
     * signed before today, which all say Lysa.
     */
    companySigner: input.repName?.trim() || COMPANY_SIGNER_NAME,
    companySignedDate: input.date,
    blocks: contractBlocks(input.contract, {
      kind,
      insurance,
      jobType: input.jobType,
      warranty,
      longerWorkmanship,
    }),
  });

  return {
    revision: input.revision ?? 1,
    date: input.date,
    contractVersion: version,
    sections,
  };
}

/** The upgrade's video, as a field to spread, or nothing. */
function upgradeVideoField(name: string): { video?: UpgradeVideo } {
  const video = upgradeVideoFor(name);
  return video ? { video } : {};
}

/**
 * The comparison chart, or null when fewer than two whole roofs are offered.
 *
 * Read off the packages exactly as the document froze them — the reworded Key
 * Differentiator, the blocks the customer reads — so the chart and the page
 * above it cannot disagree. The roof type comes from the input because the
 * frozen package does not keep it.
 */
function buildCompare(
  inputs: readonly PackageInput[],
  presented: readonly PresentedPackage[],
  upgrades: readonly UpgradeInput[],
  jobType: string | null | undefined,
): CompareSection | null {
  const roofs = new Set(keyDifferentiatorPackages(inputs));
  const comparison = buildComparison(
    presented
      .filter((pkg) => roofs.has(pkg.estimateId))
      .map((pkg) => ({
        estimateId: pkg.estimateId,
        name: pkg.name,
        headline: pkg.headline,
        roofType: inputs.find((each) => each.estimateId === pkg.estimateId)
          ?.roofType,
        // What the rep changed in this column on the preview (ADR 0318).
        edits: inputs.find((each) => each.estimateId === pkg.estimateId)
          ?.compareEdits,
        blocks: pkg.blocks,
        includedUpgrades: upgrades
          .filter((upgrade) => upgrade.includedIn?.includes(pkg.estimateId))
          .map((upgrade) => wordingFor(jobType, upgrade.name)),
        totalFormatted: pkg.totalFormatted,
        monthly: pkg.monthly,
      })),
  );
  if (!comparison) return null;
  return { id: "compare", heading: "Your Package Options", ...comparison };
}

/**
 * The gutter guard showcase, when the document offers a gutter guard.
 *
 * "Upgrade Option" is said only when the guards are an upgrade and not in any
 * package: a gutter guard estimate is the job itself, not an option on it.
 * A copy of the showcase, because the document is frozen at send (ADR 0032).
 */
function buildShowcase(
  packages: readonly PackageInput[],
  upgradeNames: readonly string[],
  companyName: string,
): ShowcaseSection | null {
  const inPackage = packages.some((pkg) =>
    packageLineNames(pkg).some(isGutterGuardProduct),
  );
  const asUpgrade = upgradeNames.some(isGutterGuardProduct);
  if (!inPackage && !asUpgrade) return null;
  const name = companyName.trim();
  const parts = [name, inPackage ? "" : "Upgrade Option"].filter(Boolean);
  const showcase = PRO_FLO_SHOWCASE;
  return {
    id: "showcase",
    heading: showcase.product,
    eyebrow: parts.join(" · "),
    showcase: {
      ...showcase,
      tagline: [...showcase.tagline],
      slides: showcase.slides.map((slide) => ({
        ...slide,
        ...(slide.points ? { points: [...slide.points] } : {}),
      })),
    },
  };
}

/**
 * The order the sections always appear in. Nothing reorders them.
 *
 * A section the rep turned off, or one with nothing in it, is absent rather
 * than empty — so a document holds a subset of this list, in this order, and
 * never a permutation of it.
 */
export const SECTION_ORDER: readonly DocumentSection["id"][] = [
  "cover",
  "findings",
  // Directly under the photographs, because the argument runs problem then
  // fix. See `VideosSection`.
  "videos",
  "scope",
  "packages",
  // The roofs side by side, once each has been read. See `CompareSection`.
  "compare",
  // What a gutter guard looks like, before the box that adds one. See
  // `ShowcaseSection`.
  "showcase",
  "upgrades",
  "financing",
  "colors",
  // Our finished jobs, the last page that sells. See `WorkSection`.
  "work",
  // The ask goes last, immediately before the agreement. See the comment on
  // the push in `buildPresentedDocument`.
  "summary",
  "contract",
];

/**
 * The blocks of a package, grouped the way the estimate says to group them.
 *
 * `estimate_items.section` is the heading a line sits under — "What You Get",
 * "Warranty", "Worry Free Wood Bonus". Lines keep the order the estimate has
 * them in, and a line with no section named lands under the first heading
 * rather than vanishing, because a line nobody can see is how a package
 * quietly loses its warranty paragraph.
 */
export interface PackageCopyLine {
  description?: string | null;
  note?: string | null;
  section?: string | null;
  work_doing?: boolean | null;
  customer_visible?: boolean | null;
  /**
   * `scope_qualifiers.id` values this line's scope calls for.
   *
   * Set by the AI draft, which judges which protective clauses the work needs
   * (ADR 0100). Ids and not sentences: the wording is Jonathon's, editable in
   * Tools, and resolved here at build time so an estimate nobody has sent yet
   * picks up a rewording.
   */
  scope_qualifiers?: readonly string[] | null;
}

/**
 * One protective clause, as Tools holds it.
 *
 * `body` is the whole of what a customer reads. `when_to_use` never leaves the
 * `scope-draft` prompt and is not part of this type, because nothing on the
 * presentation side has any business with it.
 */
export interface ScopeQualifier {
  id: string;
  body: string;
}

/**
 * The heading a package's own wording sits under.
 *
 * "Scope of Work", not "What You Get" — Jonathon, 2026-08-19. It is the list of
 * what we are actually going to do, and that is what a customer comparing two
 * quotes is looking for. The wording on the templates was renamed with it
 * (migration 20260819130000); this is what a line carrying no section of its
 * own falls under.
 */
export const DEFAULT_PACKAGE_SECTION = "Scope of Work";

/**
 * The block a rep types themselves, on the job in front of them.
 *
 * Every other block on a package is the same wording every time — it comes off
 * the template. This one is what makes the proposal about *this* house: the
 * rotten fascia over the garage, the satellite dish to come down, the gate the
 * crew cannot get a trailer through. Jonathon, 2026-08-17.
 *
 * It is a section like any other, so it needs no special handling anywhere
 * downstream; it is named here because the rep's screen offers it by name and
 * two spellings of it would be two blocks on the customer's copy.
 */
export const HOME_SPECIFIC_SECTION = "Items Specific to You and Your Home";

/**
 * Where the protective wording used to print, and still does on old copies.
 *
 * From 2026-09-06 to 2026-09-10 the clauses were collected across a package
 * and printed once under this heading at its foot (ADR 0100). Jonathon then
 * reversed the shape: "I don't want the disclaimers as a separate line item.
 * I just want to make sure they're inside the scope of work where relevant."
 * They now print inside the scope of the line that called for them
 * (`lineScopeQualifiers`, ADR 0124), and nothing writes this heading.
 *
 * Kept because a document frozen in that window carries a block under it, and
 * both renderers still read its empty-named lines as bare paragraphs.
 */
export const QUALIFIER_SECTION = "Please Note";

/**
 * What this block has been called, lower-cased.
 *
 * It was "Items Specific to Your Home" until 2026-08-19, when it took the
 * wording the Roofr packet uses. The old string is still on every estimate
 * written before then and renaming it in place would have been a rewrite of
 * live rows, so both spellings are read as this block and the current one is
 * what gets written and shown. Same reasoning as the reference values in
 * `docs/` — you add a spelling, you do not rename one.
 */
const HOME_SPECIFIC_NAMES = [
  "items specific to you and your home",
  "items specific to your home",
  // A commercial building is not a home. Jonathon, 2026-08-26: "items specific
  // to you and your property on commercial since it's not home." Read as the
  // same block, so turning it off on a job that later changes type does not
  // quietly turn it back on.
  "items specific to you and your property",
  "items specific to your property",
];

/**
 * The block that promised a schedule, which no estimate carries any more.
 *
 * Jonathon, 2026-08-27: "remove all time frame / start-date / completion-date
 * guarantee language across the app ... all job types, not just metal." The
 * paragraph offered a refund or a credit if the work did not start inside a
 * window, and then took it back for permits, materials, trade availability and
 * weather — which is everything that actually delays a roof. In this trade the
 * weather owns the schedule, and a duration in writing is a promise nobody
 * meant to make. The same reasoning banned it from the spoken-scope draft on
 * 2026-08-25.
 *
 * Migration 20260827160000 takes it off the templates and off every estimate
 * that carries one. This is the second lock: a row typed by hand, restored by
 * the mirror from an older option, or re-added to a template by somebody who
 * did not know, still never reaches a customer.
 */
const TIMEFRAME_NAMES = ["timeframe guarantees", "time frame guarantees"];

export function isTimeframeSection(
  section: string | null | undefined,
): boolean {
  return TIMEFRAME_NAMES.includes((section ?? "").trim().toLowerCase());
}

/** Whether a section heading is the rep's own block for this house. */
export function isHomeSpecificSection(
  section: string | null | undefined,
): boolean {
  return HOME_SPECIFIC_NAMES.includes((section ?? "").trim().toLowerCase());
}

/**
 * The rep's own block, worded for the building it is about.
 *
 * "Home" is wrong on a warehouse and it is wrong in a way a customer notices,
 * because it is the one heading on the page that claims to be about them. The
 * stored `section` on the row never changes — only what the document prints —
 * so an estimate written on a job that is later re-typed reads correctly the
 * next time it is presented rather than carrying yesterday's word.
 */
export function homeSpecificHeading(
  jobType: string | null | undefined,
): string {
  return isCommercialJob(jobType)
    ? "Items Specific to You and Your Property"
    : HOME_SPECIFIC_SECTION;
}

/**
 * Whether a line is wording rather than work.
 *
 * A section is what makes it wording: `roof_template_items.section` marks a
 * template line as presentation copy, the generator carries it onto
 * `estimate_items.section`, and the home-specific block writes its own. A
 * priced line never has one.
 */
export function isWordingLine(item: {
  section?: string | null | undefined;
}): boolean {
  return (item.section ?? "").trim() !== "";
}

/**
 * The lines the estimate builder shows: the work, not the wording.
 *
 * Jonathon, 2026-08-24, reviewing a roof replacement estimate: the package
 * wording showing up as line items in the builder read as clutter and a
 * reporting worry — "i would rather be able to edit the text on the
 * presentation screen and there only be one line item for those roof
 * replacement options." So the builder shows only the work, and the wording
 * is edited on the Present screen, where it is read. The wording rows still
 * exist and still carry zero money, so no total, ladder figure or report
 * moves either way — `job_contract_parts` and `computeLadder` sum prices,
 * and theirs are all $0.
 */
export function builderItems<T extends { section?: string | null | undefined }>(
  items: readonly T[],
): T[] {
  return items.filter((item) => !isWordingLine(item));
}

/** The warranty block, which the home-specific block sits directly under. */
const isWarrantySection = (heading: string): boolean =>
  heading.trim().toLowerCase().startsWith("warranty");

/** The one key every spelling of the rep's own block collapses to. */
export const HOME_SPECIFIC_KEY = "items specific to you and your home";

/**
 * How a block is named in `estimates.hidden_sections`.
 *
 * A key rather than the heading, because the heading is not stable: the
 * home-specific block has four spellings across the rename and the commercial
 * wording, and a switch that stopped working because the word "Home" became
 * "Property" would turn a block back on without anybody touching it. The key
 * is what the block *is*; the heading is what it says.
 */
export function sectionKey(heading: string | null | undefined): string {
  const raw = (heading ?? "").trim().toLowerCase();
  if (isHomeSpecificSection(raw)) return HOME_SPECIFIC_KEY;
  return raw || DEFAULT_PACKAGE_SECTION.toLowerCase();
}

/**
 * The blocks an estimate carries, for a screen that offers to switch them off.
 *
 * In the order the customer reads them, one entry per heading, and it ignores
 * `hidden_sections` entirely — a switch that vanished when you turned it off
 * could never be turned back on.
 */
export function packageSections(
  items: readonly PackageCopyLine[],
  options: {
    jobType?: string | null | undefined;
    order?: readonly string[] | null | undefined;
  } = {},
): { key: string; heading: string }[] {
  const out: { key: string; heading: string }[] = [];
  for (const item of items) {
    if (item.work_doing === false) continue;
    if ((item.description ?? "").trim() === "") continue;
    // Not offered as a switch either: it is gone, not off.
    if (isTimeframeSection(item.section)) continue;
    const own = isHomeSpecificSection(item.section);
    const stored = own
      ? homeSpecificHeading(options.jobType)
      : (item.section ?? "").trim() || DEFAULT_PACKAGE_SECTION;
    // Keyed on the heading as it is stored, worded as it will be read. A
    // switch that changed its key because a word changed would turn a block
    // back on without anybody touching it — the same trap `sectionKey`
    // already documents for the Home/Property rename.
    const key = sectionKey(stored);
    if (out.some((entry) => entry.key === key)) continue;
    out.push({
      key,
      heading: own ? stored : wordingFor(options.jobType, stored),
    });
  }
  const order = options.order ?? [];
  if (order.length > 0) return applySectionOrder(out, order);
  // The same nudge `packageBlocks` makes, so the list on the rep's screen is
  // the order the customer will read — otherwise the first press of an arrow
  // moves a block from a position it was never in.
  const at = out.findIndex((entry) => entry.key === HOME_SPECIFIC_KEY);
  const warranty = out.findIndex((entry) => isWarrantySection(entry.heading));
  const own = at > -1 ? out[at] : undefined;
  if (own && warranty > -1 && at !== warranty + 1) {
    out.splice(at, 1);
    out.splice(warranty < at ? warranty + 1 : warranty, 0, own);
  }
  return out;
}

export interface PackageBlockOptions {
  /**
   * Whether this package may carry the rep's own block for the house.
   *
   * Replacements only (Jonathon, 2026-08-19). A repair quote *is* a list of
   * items specific to the house — the block would be the whole document
   * repeated under its own heading.
   */
  homeSpecific?: boolean | undefined;
  /**
   * Blocks the rep has switched off, as `sectionKey` values.
   *
   * Jonathon, 2026-08-26: "I need to be able to toggle on and off what I want
   * visible on estimates." Left out of the document altogether rather than
   * printed empty, the same way a section toggle works — see `SectionToggles`.
   */
  hidden?: readonly string[] | null | undefined;
  /**
   * The order the rep put the blocks in, as `sectionKey` values.
   *
   * Jonathon, 2026-08-27: everything the customer reads has to be movable as
   * well as removable. Empty means the order the line items are already in,
   * which is the template's, so an estimate nobody has reordered reads exactly
   * as it did before this existed.
   *
   * A block the list does not name keeps its place relative to the others and
   * follows the ones it does — a template that grows a new block later shows
   * it rather than losing it to a stale list.
   */
  order?: readonly string[] | null | undefined;
  /**
   * What the job is, for the one heading that depends on it. Commercial reads
   * "…and Your Property"; everything else reads "…and Your Home".
   */
  jobType?: string | null;
  /**
   * The protective clauses, in the order Tools sorts them.
   *
   * The catalogue, not this package's selection — the selection lives on the
   * lines, and each line prints its own inside its scope (ADR 0124). Absent
   * means no clause anywhere, which is what every caller written before ADR
   * 0100 gets.
   */
  qualifiers?: readonly ScopeQualifier[];
}

/**
 * The clauses a package's own lines called for, each once, in catalogue order.
 *
 * Order comes from the catalogue rather than from the lines, so two packages
 * carrying the same clauses print them in the same order however their line
 * items happen to be arranged — and so `sort_order` in Tools means what it
 * says.
 *
 * An id no clause answers to is dropped rather than printed as a blank: a
 * clause deleted in Tools leaves ids behind on every estimate that used it,
 * and that is exactly the case the missing foreign key was left out for. So is
 * a clause with an empty `body`, which is a row somebody has added and not
 * finished writing.
 */
export function scopeQualifierLines(
  items: readonly PackageCopyLine[],
  catalogue: readonly ScopeQualifier[],
): string[] {
  const wanted = new Set<string>();
  for (const item of items) {
    for (const id of item.scope_qualifiers ?? []) {
      const trimmed = (id ?? "").trim();
      if (trimmed) wanted.add(trimmed);
    }
  }
  if (wanted.size === 0) return [];

  const lines: string[] = [];
  const seen = new Set<string>();
  for (const clause of catalogue) {
    if (!wanted.has(clause.id)) continue;
    const body = (clause.body ?? "").trim();
    // Two rows worded identically are one paragraph on the page. A customer
    // reading the same sentence twice reads it as a mistake, which it is.
    if (!body || seen.has(body)) continue;
    seen.add(body);
    lines.push(body);
  }
  return lines;
}

/** Lower case, letters and digits only, one space between words. */
const comparable = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * The clauses one line's scope calls for, less any it already says.
 *
 * The scope of work is typed text, and plenty of it already carries a clause
 * word for word — the products seeded on 2026-09-05 have their own leak
 * sentence typed in, and a rep may paste one. Printing the clause again under
 * the same sentence reads as a mistake. "Says it" is deliberately narrow:
 * the same words in the same order, ignoring case, punctuation and spacing.
 * A paraphrase is not caught, and printing a clause twice is the mild failure
 * next to dropping one a customer needed to read.
 */
export function lineScopeQualifiers(
  item: PackageCopyLine,
  catalogue: readonly ScopeQualifier[],
): string[] {
  const said = ` ${comparable(item.note ?? "")} `;
  return scopeQualifierLines([item], catalogue).filter(
    (clause) => !said.includes(` ${comparable(clause)} `),
  );
}

/**
 * Put blocks in the order the rep chose.
 *
 * Stable: two blocks the list does not name keep the order they arrived in,
 * and named ones sort ahead of unnamed ones rather than the list having to
 * mention every block that exists.
 */
export function applySectionOrder<T extends { heading: string }>(
  blocks: T[],
  order: readonly string[],
): T[] {
  const rank = new Map(order.map((key, at) => [sectionKey(key), at]));
  return blocks
    .map((block, at) => ({ block, at }))
    .sort((a, b) => {
      const ra = rank.get(sectionKey(a.block.heading)) ?? Infinity;
      const rb = rank.get(sectionKey(b.block.heading)) ?? Infinity;
      return ra - rb || a.at - b.at;
    })
    .map((entry) => entry.block);
}

/**
 * The heading a line is filed under, as `hidden_sections` stores it.
 *
 * Both spellings of the rep's own block land under the current one, so an
 * estimate started before the rename does not show the same block twice under
 * two headings.
 */
function storedHeading(
  item: PackageCopyLine,
  jobType: string | null | undefined,
): string {
  return isHomeSpecificSection(item.section)
    ? homeSpecificHeading(jobType)
    : (item.section ?? "").trim() || DEFAULT_PACKAGE_SECTION;
}

/**
 * Whether a line is printed in a package at all.
 *
 * The one filter `packageBlocks` and `firstLineName` share, so a package is
 * never named after a line the block under its heading leaves out. They used
 * to carry a copy each, and the copy in `firstLineName` had fallen behind: it
 * let a timeframe line name an option that no customer would ever read
 * (review of #254).
 */
export function printsInPackage(
  item: PackageCopyLine,
  options: {
    homeSpecific?: boolean;
    hidden?: ReadonlySet<string>;
    jobType?: string | null | undefined;
  } = {},
): boolean {
  if (item.work_doing === false) return false;
  if (item.customer_visible === false) return false;
  if ((item.description ?? "").trim() === "") return false;
  // Nothing promises a schedule any more, whatever a row says (2026-08-27).
  if (isTimeframeSection(item.section)) return false;
  if (isHomeSpecificSection(item.section) && options.homeSpecific === false)
    return false;
  // The switch is asked about the stored heading and the customer reads the
  // reworded one. Keying on the reworded heading would mean a block whose
  // name happens to carry one of these words had a different switch on a
  // commercial job than on a residential one.
  if (options.hidden?.has(sectionKey(storedHeading(item, options.jobType))))
    return false;
  return true;
}

export function packageBlocks(
  items: readonly PackageCopyLine[],
  options: PackageBlockOptions = {},
): PackageBlock[] {
  const homeSpecific = options.homeSpecific !== false;
  const hidden = new Set((options.hidden ?? []).map((key) => sectionKey(key)));
  const blocks: PackageBlock[] = [];
  for (const item of items) {
    if (
      !printsInPackage(item, { homeSpecific, hidden, jobType: options.jobType })
    )
      continue;
    const name = (item.description ?? "").trim();
    const own = isHomeSpecificSection(item.section);
    const stored = storedHeading(item, options.jobType);
    const heading = own ? stored : wordingFor(options.jobType, stored);
    const block = blocks.find((candidate) => candidate.heading === heading);
    /*
     * The protective wording, inside this line's own scope of work.
     *
     * Jonathon, 2026-09-10: "I don't want the disclaimers as a separate line
     * item. I just want to make sure they're inside the scope of work where
     * relevant." So a clause is a paragraph after the scope it qualifies, on
     * the line that called for it, rather than a "Please Note" block at the
     * foot of the package (ADR 0124). A line that is not on the page takes its
     * clauses with it, because it never reaches here.
     */
    const note = (item.note ?? "").trim();
    const scope = [note, ...lineScopeQualifiers(item, options.qualifiers ?? [])]
      .filter((paragraph) => paragraph !== "")
      .join("\n\n");
    const line = {
      name: wordingFor(options.jobType, name),
      body: wordingForOptional(options.jobType, scope || null),
    };
    if (block) block.lines.push(line);
    else blocks.push({ heading, lines: [line] });
  }

  /*
   * Straight under the warranty, wherever the line items happen to sit.
   *
   * A rep types this block last, so left in item order it lands at the foot of
   * the package — after the bonus and the small print, which is the one place
   * on the page a customer has stopped reading. Under the warranty it is still
   * inside the part that describes what they are buying.
   */
  // By what the block is, not by the words it happens to be wearing — on a
  // commercial job the heading reads "…and Your Property" and an exact
  // comparison against the residential wording would leave it at the foot of
  // the page, which is the one place the customer has stopped reading.
  const at = blocks.findIndex((block) => isHomeSpecificSection(block.heading));
  const warranty = blocks.findIndex((block) =>
    isWarrantySection(block.heading),
  );
  const own = at > -1 ? blocks[at] : undefined;
  if (own && warranty > -1 && at !== warranty + 1) {
    blocks.splice(at, 1);
    blocks.splice(warranty < at ? warranty + 1 : warranty, 0, own);
  }
  // An order the rep set by hand wins over that nudge — the nudge exists to
  // put the block somewhere sensible when nobody has said, and somebody has.
  const order = options.order ?? [];
  return order.length > 0 ? applySectionOrder(blocks, order) : blocks;
}

/**
 * What to call a package on the customer's copy.
 *
 * An estimate's name is the office's: "Peace of Mind Package" came off a
 * template and is exactly what the customer should read, but "Estimate 7" is a
 * counter — Jonathon, 2026-08-19: "shouldn't say estimate 7 silver pledge
 * anywhere on something like this". A from-scratch estimate and a custom repair
 * both carry one of those, because that is what the builder called them when
 * nobody had chosen a name.
 *
 * So a name that is only a number is replaced by what the thing actually is,
 * decided by the trade the estimate carries. A real name is never touched.
 *
 * **A blank trade is not a roof.** An estimate written from scratch carries no
 * trade at all, and reading that as a replacement put "Your New Roof" at the
 * top of a chimney siding repair (Samuel, 2026-08-21). So when the estimate
 * itself has no opinion, the document's own `kind` answers — the same
 * repair-or-replacement question `contractKindFor` already settles for the
 * agreement at the bottom, from the trades and then the job's bid type. It
 * defaults to a replacement only because that is what `contractKindFor` does
 * with nothing to go on.
 */
export function packageNameFor(
  estimate: {
    name?: string | null;
    trade?: string | null;
  },
  kind: ContractKind = "replacement",
  items: readonly PackageCopyLine[] = [],
): string {
  const name = (estimate.name ?? "").trim();
  if (!isCounterName(name)) return name;
  // What the rep called the first line, before what the thing is in general.
  // Jonathon, 2026-09-15: "i do want it to show the name of the product if we
  // named the product." "Your New Roof" is what to say when nobody has said
  // anything; a line reading "Standing Seam Metal Roof System" is somebody
  // having said something.
  const line = firstLineName(items);
  if (line) return line;
  if (isRepairTrade(estimate.trade)) return "Your Repair";
  if (isReplacementTrade(estimate.trade)) return "Your New Roof";
  return kind === "repair" ? "Your Repair" : "Your New Roof";
}

/**
 * Whether a name is a counter rather than a name.
 *
 * "Estimate 2", "Option 3", "Package" — what the builder writes when nobody
 * has chosen anything. One copy of the test, because two screens and the
 * customer's document all have to agree on which names are real.
 */
export function isCounterName(name: string | null | undefined): boolean {
  const trimmed = (name ?? "").trim();
  return trimmed === "" || /^(estimate|option|package)\s*\d*$/i.test(trimmed);
}

/**
 * What the first line on an estimate is called, or null if it has none.
 *
 * The same lines, in the same order, through the same filter `packageBlocks`
 * uses (`printsInPackage`) — a line the customer never sees cannot be what the
 * option is called, and neither can a line nobody has typed a name into.
 * Callers pass the items in `sort_order`, which is the order every query
 * already fetches them in and the order the builder lists them.
 *
 * The rep's own block for the house is skipped on every kind of job, not only
 * on a repair where `packageBlocks` leaves it out: it is about the building,
 * never what is being sold, and it prints under the warranty wherever it sits
 * in the list.
 */
export function firstLineName(
  items: readonly PackageCopyLine[],
): string | null {
  for (const item of items) {
    if (!printsInPackage(item, { homeSpecific: false })) continue;
    return (item.description ?? "").trim();
  }
  return null;
}

/**
 * What to call an estimate on our own screens — the builder's tabs, the
 * Present list, the copy-from picker.
 *
 * Jonathon, 2026-09-15: "instead of estimate 1 / estimate 2 / estimate 3 it's
 * name can be whatever we named the first line item on the estimate builder.
 * if there's not a name then it can be estimate 1, 2, 3 etc." Three options on
 * a job used to read "Estimate 1", "Estimate 2", "Estimate 3", which says
 * nothing about which is the metal one — and the rep picking between them in
 * front of a customer is the person who needs to know.
 *
 * Not `packageNameFor`, which answers the same question for the customer and
 * falls back to "Your New Roof". Here the fallback is the counter, because on
 * our own side a number that tells the options apart beats a phrase that does
 * not. Not `estimateLabel` in mirror.ts either, which is the same question
 * asked where no line items are in hand and answers "Version 2".
 */
export function estimateOptionLabel(
  estimate: { name?: string | null; version?: number | null },
  items: readonly PackageCopyLine[] = [],
): string {
  const name = (estimate.name ?? "").trim();
  if (!isCounterName(name)) return name;
  return (
    firstLineName(items) ??
    (name || `Estimate ${estimate.version ?? ""}`.trim())
  );
}

/**
 * Names made distinct: the second of a name reads "Name (2)", the third
 * "Name (3)", in the order given. The first keeps its name as it is.
 *
 * Two options that both start with "Tear off and haul away" got identical tabs
 * and identical headings on the customer's copy, which leaves a customer
 * choosing between two things with nothing to say which is which (review of
 * #254). Names compare ignoring case and spacing, because "Tear Off" and
 * "tear off" are the same name to anybody reading them. A suffix never lands
 * on a name already in the list, so a package somebody called "Metal (2)" is
 * not doubled either.
 *
 * The one place this is decided — the builder's tabs, the Present list, the
 * copy-from picker and the presented document all come through here.
 */
export function distinguishNames(names: readonly string[]): string[] {
  const key = (name: string) => name.trim().replace(/\s+/g, " ").toLowerCase();
  const originals = new Set(names.map(key));
  const used = new Set<string>();
  const seen = new Map<string, number>();
  return names.map((name) => {
    const k = key(name);
    const count = (seen.get(k) ?? 0) + 1;
    seen.set(k, count);
    if (count === 1) {
      used.add(k);
      return name;
    }
    let n = count;
    let candidate = `${name.trim()} (${n})`;
    while (used.has(key(candidate)) || originals.has(key(candidate))) {
      n += 1;
      candidate = `${name.trim()} (${n})`;
    }
    used.add(key(candidate));
    return candidate;
  });
}

/**
 * Every estimate's `estimateOptionLabel`, made distinct, by id.
 *
 * Numbered in version order whatever order the caller lists them in, so the
 * same option is "(2)" on every screen.
 */
export function estimateOptionLabels(
  estimates: readonly {
    id: string;
    name?: string | null;
    version?: number | null;
  }[],
  lines: ReadonlyMap<string, readonly PackageCopyLine[]>,
): Map<string, string> {
  const ordered = [...estimates].sort(
    (a, b) => (a.version ?? 0) - (b.version ?? 0),
  );
  const labels = distinguishNames(
    ordered.map((e) => estimateOptionLabel(e, lines.get(e.id) ?? [])),
  );
  return new Map(ordered.map((e, at) => [e.id, labels[at]!]));
}

/** The little of an estimate that deciding whether to offer it depends on. */
export interface OfferableEstimate {
  id: string;
  version: number;
  offer_on_proposal?: boolean | null | undefined;
}

/**
 * Which estimates Present Estimate should offer, in the order to read them.
 *
 * The set is the rep's, decided in the estimate builder rather than re-answered
 * at send (Jonathon, 2026-08-15: "on the estimate builder we will toggle off or
 * delete the estimates we don't want to present"). Version order, because that
 * is the order they were built in and the order the builder lists them.
 *
 * `offer_on_proposal` missing counts as offered. Every row predates the column
 * and its default is true, so a null here means "nobody has said otherwise"
 * rather than "no".
 *
 * **`current` is the floor.** If every estimate on the job has been toggled
 * off, the one the rep pressed Present on is offered anyway. The alternative is
 * refusing to present at all over a setting the rep may have made weeks ago,
 * in front of a customer — the same reasoning that stopped contract blanks
 * blocking a send.
 */
export function estimatesToOffer<T extends OfferableEstimate>(
  estimates: readonly T[],
  current: T,
): T[] {
  const offered = estimates
    .filter((estimate) => estimate.offer_on_proposal !== false)
    .sort((a, b) => a.version - b.version);
  return offered.length > 0 ? offered : [current];
}

/**
 * The packages a document offers, or an empty list when it quotes one price.
 *
 * Both the customer's page and the endpoint that accepts their signature ask
 * this question, so it is answered here rather than by each of them poking at
 * `sections` and getting it subtly differently.
 */
export function documentPackages(
  document: PresentedDocument,
): PresentedPackage[] {
  const section = document.sections.find(
    (candidate): candidate is PackagesSection => candidate.id === "packages",
  );
  return section?.packages ?? [];
}

/**
 * The order a packet's options are read in: most expensive first, unless the
 * rep moved them.
 *
 * Jonathon, 2026-10-20: "we need to be able to sort how estimates show on the
 * estimate. it should typically start with most expensive and work it's way
 * down" (ADR 0294). So the price decides until somebody presses an arrow, and
 * then what they put on screen is the order.
 *
 * `chosen` is every id in the order the rep left them, or empty. An option it
 * does not name — one ticked after the rep last moved anything — goes after
 * the ones it does, most expensive first among themselves. A price nobody
 * could work out yet (`null`) sorts last; two equal prices keep the order they
 * came in, which is version order.
 */
export function packetOrder<T extends { id: string; total: Cents | null }>(
  options: readonly T[],
  chosen: readonly string[] = [],
): T[] {
  const rank = new Map(chosen.map((id, at) => [id, at]));
  return options
    .map((option, at) => ({ option, at }))
    .sort((a, b) => {
      const ra = rank.get(a.option.id) ?? Infinity;
      const rb = rank.get(b.option.id) ?? Infinity;
      if (ra !== rb) return ra - rb;
      const ta = a.option.total ?? -Infinity;
      const tb = b.option.total ?? -Infinity;
      return tb - ta || a.at - b.at;
    })
    .map((entry) => entry.option);
}

/**
 * The order that decides which of several bought options is the main one:
 * version order, the oldest estimate first.
 *
 * That is how it was decided before a packet's options could be sorted, when
 * the reading order was version order too. Sorting changed what the customer
 * reads and nothing else, so this stays apart from `packetOrder` and is frozen
 * into the document as `leadOrder` (ADR 0294).
 */
export function mainEstimateOrder(
  options: readonly { id: string; version: number }[],
): string[] {
  return options
    .map((option, at) => ({ option, at }))
    .sort((a, b) => a.option.version - b.option.version || a.at - b.at)
    .map((entry) => entry.option.id);
}

/**
 * Packages put in the order that decides the main one: the document's
 * `leadOrder` when it froze one, otherwise its own order, which is what every
 * document frozen before 2026-10-22 decided by. An id the lead order does not
 * name keeps its place after the ones it does.
 */
function inLeadOrder(
  document: PresentedDocument,
  packages: readonly PresentedPackage[],
): PresentedPackage[] {
  const section = document.sections.find(
    (candidate): candidate is PackagesSection => candidate.id === "packages",
  );
  const rank = new Map(
    (section?.leadOrder ?? []).map((id, at) => [id, at] as const),
  );
  return packages
    .map((pack, at) => ({ pack, at }))
    .sort(
      (a, b) =>
        (rank.get(a.pack.estimateId) ?? Infinity) -
          (rank.get(b.pack.estimateId) ?? Infinity) || a.at - b.at,
    )
    .map((entry) => entry.pack);
}

/**
 * The package a customer picked, looked up the only way that is safe.
 *
 * By `estimateId` against the document's own list — never by index, and never
 * by trusting a total that arrived with the request. What the customer sends
 * is a choice between things we offered; the price of that choice is ours.
 */
export function chosenPackage(
  document: PresentedDocument,
  estimateId: Chosen,
): PresentedPackage | null {
  return chosenPackages(document, estimateId)[0] ?? null;
}

/**
 * What the customer has picked, however many that is.
 *
 * One id or a list of them: a document that offers a choice between packages
 * takes one, and one whose job is `multi_option` takes as many as they tick
 * (ADR 0120). Every function below reads a choice through `chosenIds`, so
 * neither the page nor the server has to care which kind of document it is
 * holding.
 */
export type Chosen = string | readonly string[] | null | undefined;

/**
 * A choice as a list, de-duplicated, with the empty strings dropped.
 *
 * Deliberately forgiving about what it is handed and strict about what it
 * returns: what arrives can be a single id from an old page, a list from a new
 * one, or a request somebody typed themselves. What comes out is a list of ids
 * to look up — and looking them up against the document is what decides whether
 * they buy anything.
 */
export function chosenIds(chosen: Chosen): string[] {
  const list = typeof chosen === "string" ? [chosen] : (chosen ?? []);
  return [...new Set(list.filter((id) => typeof id === "string" && id !== ""))];
}

/** Whether this document lets the customer buy more than one package. */
export function documentChoosesMany(document: PresentedDocument): boolean {
  const section = document.sections.find(
    (candidate): candidate is PackagesSection => candidate.id === "packages",
  );
  return Boolean(section?.chooseMany) && (section?.packages.length ?? 0) > 1;
}

/**
 * Whether a document built from these packages sells several things.
 *
 * `jobs.multi_option`, and at least two of the packages that could be bought
 * together (`canBuyTogether`) — the one answer `buildPresentedDocument` freezes
 * as `chooseMany`. With `roofsTogether` (the box set by hand, ADR 0124) two
 * roofs count as two things that could be bought together.
 *
 * Whole roofs by `isWholeRoofOption`, the answer `wholeRoof` is frozen from,
 * so a roof with no trade of its own is a rival here exactly as it is on the
 * customer's page.
 */
export function documentSellsSeveral(
  multiOption: boolean | null | undefined,
  packages: readonly RoofOption[],
  roofsTogether = false,
): boolean {
  return (
    Boolean(multiOption) &&
    anyTwoTogether(
      packages.map((candidate) => isWholeRoofOption(candidate)),
      roofsTogether,
    )
  );
}

/**
 * Whether a document built from these would let the customer buy two or more
 * whole roofs together.
 *
 * The box set by hand (`roofsBuyableTogether`), a document that sells several
 * things with that permission (`documentSellsSeveral`, which is when
 * `roofsTogether` is frozen), and at least two whole roofs on it. That is Jonathon's
 * house and detached garage (ADR 0124), and it is also two alternatives for
 * the same house, which the customer can then both buy — the permission covers
 * the whole document. The box looks the same either way, so the Present screen
 * says so (`ROOFS_TOGETHER_NOTICE`) while there is still time to untick it.
 *
 * Asked before the document exists. `documentSellsSeveralRoofs` is the same
 * question asked of the document once it is built, and they agree.
 */
export function sellsSeveralRoofs(
  multiOption: boolean | null | undefined,
  multiOptionManual: boolean | null | undefined,
  packages: readonly RoofOption[],
): boolean {
  return (
    roofsBuyableTogether(multiOption, multiOptionManual) &&
    documentSellsSeveral(multiOption, packages, true) &&
    packages.filter((candidate) => isWholeRoofOption(candidate)).length >= 2
  );
}

/**
 * Whether a document built from these carries the chart comparing its roofs
 * (`CompareSection`, ADR 0274), before the rep's switch for one send.
 *
 * Two or more whole roofs (`isWholeRoofOption`, the Key Differentiator's
 * rule), unless the rep marked them for different buildings — a document that
 * lets the customer buy them together (`roofsTogether`) offers no choice
 * between them to chart. The document is built through it, and the Present
 * screen asks it whether to offer the switch, so the switch is there exactly
 * when there is a chart to switch off.
 */
export function documentComparesRoofs(
  multiOption: boolean | null | undefined,
  multiOptionManual: boolean | null | undefined,
  packages: readonly RoofOption[],
): boolean {
  if (packages.filter((each) => isWholeRoofOption(each)).length < 2) {
    return false;
  }
  const roofsTogether = roofsBuyableTogether(multiOption, multiOptionManual);
  return !(
    roofsTogether && documentSellsSeveral(multiOption, packages, roofsTogether)
  );
}

/**
 * Whether this document lets the customer buy two or more whole roofs.
 *
 * Off what was frozen — `roofsTogether` and each package's `wholeRoof` — so
 * the Present screen's preview warns about exactly the document it is about to
 * send. See `sellsSeveralRoofs`.
 */
export function documentSellsSeveralRoofs(
  document: PresentedDocument,
): boolean {
  return (
    documentRoofsTogether(document) &&
    documentPackages(document).filter((pkg) => pkg.wholeRoof).length >= 2
  );
}

/**
 * What the rep is told on the Present screen when `sellsSeveralRoofs`.
 *
 * Names the box by its own words and where it is, because unticking it is the
 * one thing to do about it.
 */
export const ROOFS_TOGETHER_NOTICE =
  "The customer can buy more than one of these roofs together. Untick " +
  '"Customer can buy more than one of these" on the estimate card if they ' +
  "are alternatives for the same roof.";

/**
 * The line under the Present screen's list of estimates, saying what the
 * customer will be able to do with them.
 *
 * It said "the customer picks one and signs it; the others are not sold"
 * whatever the job's box said, which stopped being true on 2026-09-09
 * (ADR 0120) and was the opposite of true for two roofs set together by hand
 * (ADR 0124). The same three answers the document is built from.
 */
export function packetSummary(
  multiOption: boolean | null | undefined,
  multiOptionManual: boolean | null | undefined,
  packages: readonly RoofOption[],
): string {
  const count = packages.length;
  if (count < 2) {
    return "One estimate is being presented, so the customer sees a price rather than a choice. Tick another to offer packages.";
  }
  const roofsTogether = roofsBuyableTogether(multiOption, multiOptionManual);
  if (!documentSellsSeveral(multiOption, packages, roofsTogether)) {
    return `${count} packages. The customer picks one and signs it; the others are not sold, and nothing is pre-selected for them.`;
  }
  const roofs = packages.filter((candidate) =>
    isWholeRoofOption(candidate),
  ).length;
  const which =
    roofs < 2
      ? "any of them together"
      : roofsTogether
        ? "any of them together, more than one roof included"
        : "any of them together, but only one of the roofs";
  return `${count} packages. The customer can buy ${which}, and signs once for everything they pick. Nothing is pre-selected for them.`;
}

/**
 * Whether this document lets the customer buy two whole roofs together.
 *
 * Off the frozen document, never off the job (ADR 0032), and only on one that
 * sells several things at all. Absent — every document sent before
 * 2026-09-10 — is no.
 */
export function documentRoofsTogether(document: PresentedDocument): boolean {
  const section = document.sections.find(
    (candidate): candidate is PackagesSection => candidate.id === "packages",
  );
  return section?.roofsTogether === true && documentChoosesMany(document);
}

/**
 * What they have chosen after tapping one package.
 *
 * On an ordinary document the one tapped is the answer and the last one goes.
 * On one that sells several things (ADR 0120) a tap adds or takes away — except
 * that whole roofs are rivals: ticking one roof un-ticks any other roof, and
 * leaves the gutters alone. Jonathon's reason for the default holds on the
 * page too: "a customer wouldn't buy multiple roof systems for the same
 * house", and a job whose box is on for the gutters can still carry two roofs.
 *
 * Unless the document says `roofsTogether`: a rep ticked the box by hand, and
 * the roofs are a house and a detached garage (ADR 0124). Then a roof is added
 * like anything else.
 *
 * Here rather than in the component so the Choose buttons and the rows in the
 * summary cannot disagree, and so the rule is next to the one the server
 * applies (`chooseOptions` in presented-document/validation.ts refuses two).
 */
export function toggleChoice(
  document: PresentedDocument,
  chosen: readonly string[],
  estimateId: string,
): string[] {
  if (!documentChoosesMany(document)) return [estimateId];
  if (chosen.includes(estimateId)) {
    return chosen.filter((id) => id !== estimateId);
  }
  const packages = documentPackages(document);
  const picked = packages.find(
    (candidate) => candidate.estimateId === estimateId,
  );
  const rivals = new Set(
    picked?.wholeRoof && !documentRoofsTogether(document)
      ? packages
          .filter((candidate) => candidate.wholeRoof)
          .map((candidate) => candidate.estimateId)
      : [],
  );
  return [...chosen.filter((id) => !rivals.has(id)), estimateId];
}

/**
 * The packages behind a choice, in the order the document offers them.
 *
 * The document's order, never the order the ids arrived in: the letter, the
 * summary and the signed record all read off this, and a customer who ticked
 * the gutters first should not see their document rearranged around that.
 * An id the document never offered buys nothing, which is the same rule the
 * server applies to a forged request.
 */
export function chosenPackages(
  document: PresentedDocument,
  chosen: Chosen,
): PresentedPackage[] {
  const wanted = new Set(chosenIds(chosen));
  return documentPackages(document).filter((candidate) =>
    wanted.has(candidate.estimateId),
  );
}

/**
 * What an upgrade costs on this roof.
 *
 * Quantity comes from `measuredQuantity` — the same arithmetic that fills the
 * box when a rep adds the product to an order by hand, so the gutters on a
 * proposal and the gutters on the work order that follows it are the same
 * number of feet. Price is quantity times the product's own price, which is
 * already retail (ADR 0013 turns cost into price in the catalogue, not here).
 *
 * Null when the roof cannot answer — no measurement, no coverage, or nothing
 * measured. An upgrade nobody can price is not offered rather than offered at
 * nothing.
 *
 * `qty` is the rep's own count, typed on the Present pop-up over the measured
 * one. Jonathon, 2026-09-30: "we need to be able to edit the quantities on the
 * upgrades on the pop up on the estimate builder." Anything that is not a
 * count above zero is ignored and the measured quantity stands, so a box
 * cleared halfway through typing never prices an upgrade at nothing.
 */
export function priceUpgrade(
  product: Pick<GeneratorProduct, "coverage" | "waste" | "measurement"> & {
    default_price?: unknown;
  },
  measurements: RoofLayouts | null | undefined,
  qty?: number | null,
): { price: Cents; qty: number } | null {
  const unit = Number(product.default_price ?? 0);
  if (!Number.isFinite(unit) || unit <= 0) return null;
  if (qty != null && Number.isFinite(qty) && qty > 0) {
    return { price: toCents(unit * qty), qty };
  }
  const measured = measuredQuantity(product, measurements);
  // Nothing to measure it against is not the same as nothing to sell. A
  // chimney chase cover is one chase cover at one price whatever the roof is,
  // and before 2026-08-26 an upgrade like that could be added to the catalogue
  // and would then silently never appear on a proposal. Measured upgrades are
  // untouched: all four of the original ones carry a measurement key.
  if (!measured) return { price: toCents(unit), qty: 1 };
  return { price: toCents(unit * measured.qty), qty: measured.qty };
}

/**
 * Why an upgrade would not be offered, or null when it is ready.
 *
 * The proposal drops an upgrade it cannot price rather than showing "$0.00",
 * which is right and is also invisible: somebody adds one under Tools, it never
 * appears on a packet, and nothing anywhere says why. This is that sentence.
 */
export function upgradeReadiness(product: {
  name?: string | null;
  default_price?: unknown;
  active?: boolean | null;
}): string | null {
  const price = Number(product.default_price ?? 0);
  if (!Number.isFinite(price) || price <= 0) {
    return "Needs a price before it can be offered.";
  }
  if (product.active === false) {
    return "Inactive, so it is not offered.";
  }
  return null;
}

/**
 * Why an upgrade cannot be renamed, or null when nothing is in the way.
 *
 * `roof_templates.included_upgrades` and `estimates.included_upgrades` hold
 * product *names* — a deliberate copy rather than a join, so that re-pricing a
 * template never changes what a job already quoted (migration 20260815130000).
 * The cost of that is exactly rule 8: renaming an upgrade orphans every list
 * carrying the old string, and the packages that used to include it start
 * charging for it without a word.
 */
export function upgradeRenameWarning(
  currentName: string,
  newName: string,
  includedBy: readonly string[],
): string | null {
  if (currentName.trim() === newName.trim()) return null;
  if (includedBy.length === 0) return null;

  const unique = [...new Set(includedBy)].sort();
  const list = unique.map((name) => `  • ${name}`).join("\n");

  return (
    `"${currentName}" is listed as already included by:\n\n${list}\n\n` +
    `Those lists hold the name, not a link, so renaming it here would make ` +
    `them charge for an upgrade they currently give away. Add a new upgrade ` +
    `under the name you want and set this one inactive instead.`
  );
}

/** The upgrades a document offers, in the order it offers them. */
export function documentUpgrades(
  document: PresentedDocument,
): PresentedUpgrade[] {
  const section = document.sections.find(
    (candidate): candidate is UpgradesSection => candidate.id === "upgrades",
  );
  return section?.upgrades ?? [];
}

/**
 * Whether what they have chosen already covers this upgrade.
 *
 * Any of it, on a document where they may buy more than one: an upgrade the
 * gutters package includes is on the roof once they have bought the gutters,
 * whether or not the other thing they ticked includes it too. Charging for it
 * because a second package does not include it would charge them for something
 * they are already getting.
 */
export function upgradeIsIncluded(
  upgrade: PresentedUpgrade,
  chosen: Chosen,
): boolean {
  return chosenIds(chosen).some((id) => upgrade.includedIn.includes(id));
}

/**
 * The upgrades that come with a package, whether the customer asked or not.
 *
 * Ticked on their behalf the moment they choose that package, and never
 * untickable: unticking would not take a penny off, because the package price
 * already contains it. Returning the keys rather than doing the ticking keeps
 * the rule here instead of in a component.
 */
export function includedUpgradeKeys(
  document: PresentedDocument,
  chosen: Chosen,
): string[] {
  return documentUpgrades(document)
    .filter((upgrade) => upgradeIsIncluded(upgrade, chosen))
    .map((upgrade) => upgrade.key);
}

/**
 * What the ticked upgrades add.
 *
 * An upgrade the chosen package already includes adds nothing, however it came
 * to be ticked — that is the rule the whole feature turns on, and it is applied
 * here rather than trusted to whoever assembled the list of keys. An unknown
 * key adds nothing either: what a customer sends is a choice between things we
 * offered.
 */
export function upgradesTotal(
  document: PresentedDocument,
  chosen: Chosen,
  chosenKeys: readonly string[],
): Cents {
  const ticked = new Set(chosenKeys);
  return documentUpgrades(document)
    .filter(
      (upgrade) =>
        ticked.has(upgrade.key) && !upgradeIsIncluded(upgrade, chosen),
    )
    .reduce((total, upgrade) => addCents(total, upgrade.price), 0 as Cents);
}

/**
 * What a document asks for, once the customer has chosen.
 *
 * One price document → its investment total. A packet → the chosen package's
 * total, and `null` until they have chosen, because a packet with nothing
 * selected has no total and saying zero would be a lie the sign bar repeats.
 *
 * Not `documentTotal` — `ladder.ts` already owns that name for the total of a
 * cost document, which is a different number about a different thing.
 */
export function presentedTotal(
  document: PresentedDocument,
  chosen?: Chosen,
  chosenUpgrades: readonly string[] = [],
): { total: Cents; totalFormatted: string } | null {
  const base = basePrice(document, chosen);
  if (base === null) return null;
  const total = addCents(base, upgradesTotal(document, chosen, chosenUpgrades));
  return { total, totalFormatted: formatMoney(total) };
}

/**
 * The roofs on their own, before anything is ticked.
 *
 * They add up. On an ordinary document there is only ever one to add, because
 * the page will not let a second be picked; on a `multi_option` one adding is
 * the whole point — the customer is buying a roof *and* the gutters, not
 * choosing between them (ADR 0030).
 */
function basePrice(document: PresentedDocument, chosen?: Chosen): Cents | null {
  const bought = boughtPackages(document, chosen);
  if (bought === null) return null;
  return bought.reduce(
    (total, pack) => addCents(total, pack.total),
    0 as Cents,
  );
}

/**
 * The packages a choice buys, the main one first (`inLeadOrder`), or null
 * while it buys nothing yet. The total, the deposit and the schedule all read
 * this, so they cannot disagree about what was bought, and the first carries
 * the upgrades exactly as signing puts them on it (`chooseOptions`).
 */
function boughtPackages(
  document: PresentedDocument,
  chosen?: Chosen,
): PresentedPackage[] | null {
  const packages = documentPackages(document);
  if (packages.length === 0) return null;
  // One package and no choice made is not an unanswered question: there was
  // nothing to answer. A packet with several is null until they pick.
  if (packages.length === 1 && chosenIds(chosen).length === 0) {
    return [packages[0]!];
  }
  const picked = chosenPackages(document, chosen);
  return picked.length === 0 ? null : inLeadOrder(document, picked);
}

/** A square count found on a customer's copy, and where it is. */
export interface SquareCountLeak {
  /** The matched text, as the customer would read it — "1.69 SQ". */
  text: string;
  /**
   * Where it is, in words a rep can act on: "Scope of Work", "Line items",
   * "Key Differentiator". The old message said "take it out of the line items"
   * whether it was there or not, and on Monica Marsh's repair it was not —
   * which left somebody deleting the right words from the wrong box.
   */
  where: string;
}

/**
 * Every number of squares on a customer's copy. Should normally be empty.
 *
 * Matches "32 SQ", "3.5 squares", "Squares: 40" — a count paired with the unit.
 * "100 square feet" is deliberately not a match: that is an area in the units a
 * homeowner actually thinks in, and it is how the layer charge is quoted.
 *
 * **This warns; it no longer refuses.** Until 2026-09-08 a match stopped a
 * proposal being presented at all, on the reasoning that there is no reading of
 * "send it anyway" that is right. Jonathon overruled that from a doorstep:
 * Monica Marsh's 1.69-square repair could not be presented, and "i don't want
 * it to block us but i do want it to warn us. if we approve it we should be
 * able to move forward."
 *
 * He is right about the cost of the two mistakes. A square count on a proposal
 * invites one awkward question; a rep who cannot hand a customer anything is a
 * job lost in front of them. So the guard stays, loudly, and the rep decides.
 */
export function squareCountLeaks(
  document: PresentedDocument,
): SquareCountLeak[] {
  const found: SquareCountLeak[] = [];
  const pattern =
    /(\b\d+(?:\.\d+)?\s*(?:sq\b|squares?\b)(?!\s*(?:feet|foot|ft))|\bsquares?\s*[:=]\s*\d)/gi;

  const walk = (value: unknown, where: string): void => {
    if (typeof value === "string") {
      for (const match of value.matchAll(pattern)) {
        found.push({ text: match[0].trim(), where });
      }
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((entry) => walk(entry, where));
      return;
    }
    if (value && typeof value === "object") {
      // The nearest thing with a name becomes the location, so a match deep
      // inside a block is reported as the block a rep can see and edit rather
      // than as a path through the JSON.
      const named = value as Record<string, unknown>;
      const label = [named.heading, named.title, named.name, named.id].find(
        (candidate): candidate is string =>
          typeof candidate === "string" && candidate.trim().length > 0,
      );
      const here = label ? label.trim() : where;
      for (const entry of Object.values(named)) {
        // A match in the label itself is reported at the ENCLOSING place, not
        // at itself: "Tear off 32 SQ" is a line whose own name carries the
        // count, and saying it is inside "Tear off 32 SQ" tells a rep nothing.
        //
        // Skipping the label instead of re-pointing it was the first attempt
        // and it silently lost exactly this case — the commonest one there is,
        // because a scope line IS its name.
        const isLabel = typeof entry === "string" && entry === label;
        walk(entry, isLabel ? where : here);
      }
    }
  };

  walk(document, "the document");
  return found;
}

/** The one sentence a screen shows about a document carrying square counts. */
export function squareCountWarning(leaks: readonly SquareCountLeak[]): string {
  if (leaks.length === 0) return "";
  const listed = leaks.map((leak) => `${leak.text} (${leak.where})`).join(", ");
  return (
    `This document shows the customer a square count: ${listed}. Squares are ` +
    "how the company prices a roof, not a number a homeowner can check, so " +
    "they normally stay off a proposal."
  );
}

/**
 * What this customer is being asked for at signing.
 *
 * Null when the document carries no deposit block (the rep turned payments
 * off), and null until a package has been chosen on a document that offers
 * several — a deposit for a roof nobody has picked is a number with nothing
 * behind it.
 *
 * The figure is the one signing bills, by the same rule:
 * `signingFirstPaymentTyped` over what they bought, each package carrying the
 * first payment the rep typed as the document froze it. The main package goes
 * first (`leadOrder`) and carries every upgrade, which is how the Edge
 * Function divides the contract (`paymentPlan`) — so an upgrade never moves a
 * typed figure, and one on an untyped package is halved with it. Until
 * 2026-09-10 this was half of the whole total whatever the rep had typed, and
 * the proposal disagreed with the bill on every estimate carrying a typed
 * first payment.
 *
 * On a document that sells several things this is one figure for what is
 * ticked, recomputed as they tick — never a figure per option. The rule is
 * combined: $800 of gutters and $900 of siding ask for nothing each and $850
 * together, so figures per option would add up to something signing never
 * bills.
 *
 * The sentence is `depositFor`'s, which names the 50% only when the figure is
 * the half.
 */
export function presentedDeposit(
  document: PresentedDocument,
  chosen?: Chosen,
  chosenUpgrades: readonly string[] = [],
): Deposit | null {
  const summary = document.sections.find(
    (section): section is SummarySection => section.id === "summary",
  );
  if (!summary?.deposit) return null;
  const bought = boughtPackages(document, chosen);
  if (bought === null) return null;
  const upgrades = upgradesTotal(document, chosen, chosenUpgrades);
  const options = bought.map((pack, index) => ({
    total: index === 0 ? addCents(pack.total, upgrades) : pack.total,
    typedFirst: pack.typedFirstPayment ?? null,
  }));
  const total = addCents(...options.map((option) => option.total));
  return depositFor(total, signingFirstPaymentTyped(options).amount);
}

/** One line of the payment schedule as the customer's document prints it. */
export interface PresentedScheduleRow {
  slot: number;
  /** "First Payment", "Final Payment". */
  label: string;
  amount: Cents;
  amountFormatted: string;
  /** "Due Upon Contract Signed", or whatever the rep wrote beside it. */
  triggerText: string;
}

/** Whether anything about these packages' payments is worth a table. */
function scheduleWorthPrinting(
  bought: readonly PresentedPackage[],
  plan: PlanTerms | null,
): boolean {
  return (
    plan !== null || bought.some((pack) => paymentsAreCustom(pack.schedule))
  );
}

/**
 * One row of the printed schedule.
 *
 * On an in-house payment plan the Final is paid monthly: the row says how, in
 * the words the rep read on the builder (ADR 0286).
 */
function scheduleRow(
  slot: number,
  amount: Cents,
  plan: PlanTerms | null,
  triggerFor: (slot: number) => string,
): PresentedScheduleRow {
  // Terms alone when the count cannot be worked out: a plan job's Final is
  // never printed as one payment.
  const wording =
    plan && slot === FINAL_SLOT
      ? (planWording(amount, plan) ?? planTermsWording(plan))
      : null;
  if (wording && plan) {
    return {
      slot,
      label: "Payment Plan",
      amount,
      amountFormatted: formatMoney(amount),
      triggerText: wording,
    };
  }
  return {
    slot,
    label: PAYMENT_SLOT_LABEL[slot] ?? `Payment ${slot}`,
    amount,
    amountFormatted: formatMoney(amount),
    triggerText: triggerFor(slot),
  };
}

/**
 * Why a package's payment plan would not print in full, or null.
 *
 * A plan whose numbers do not work (nothing left after the down payment, or
 * more than twenty years) would print its terms with no count and no last
 * date, so the rep is told to fix it before presenting (ADR 0286). Asked of
 * each package as though it were bought alone; a bought set that only fails
 * together still prints the plan's terms (`planTermsWording`).
 */
export function planWontPrint(document: PresentedDocument): string | null {
  const packages = documentPackages(document);
  for (const pack of packages) {
    if (!pack.plan) continue;
    const schedule = presentedSchedule(document, pack.estimateId);
    const final = schedule?.find((row) => row.slot === FINAL_SLOT)?.amount;
    const why =
      final === undefined
        ? "The payment schedule has an error."
        : planProblem(final, pack.plan);
    if (why === null) continue;
    const whose = packages.length > 1 ? `${pack.name}: ` : "";
    return `${whose}The payment plan would not print on the proposal. ${why} Fix the plan or take it off before sending.`;
  }
  return null;
}

/**
 * The payment schedule the customer reads, laid out against what they chose.
 *
 * Printed only when the rep wrote one (`paymentsAreCustom`): an amount typed
 * into any of the first three slots, or wording of their own on any slot.
 * Jonathon, 2026-10-05: "anytime a payment structure is created it needs to
 * reflect on the estimate" — Arquette Montgomery's proposal said "the balance
 * is due when the work is complete" under a schedule that split it three
 * ways and finished "Paid in Increments". An estimate left on the standard
 * split prints the deposit sentence alone, as it always has, and a document
 * frozen before the schedule was carries none and prints none (ADR 0032).
 *
 * Null, too, when the document has no deposit block, until a package has been
 * chosen on one that offers several, and when the typed figures add up to
 * more than the contract — an errored schedule is never shown (ADR 0031).
 *
 * The same rules as `paymentSchedule` on the builder: the First is what
 * signing bills (`signingFirstPaymentTyped`, so this line and the deposit
 * sentence above it cannot disagree), the Second and Third are what was
 * typed, and the Final is the remainder — so the rows add up to the total,
 * upgrades included, by construction. On a document that sells several
 * things the typed slots are added across what is ticked and the wording is
 * the first bought package's, the way the deposit is combined. Under $1,000
 * there is no split: one line, due on completion. Rows standing at zero are
 * left off, apart from the Final.
 *
 * Under the split, `paymentSchedule` returns an error while amounts are still
 * typed in the first three slots, so the rep clears them. Here that is no
 * schedule at all until they do. Jonathon, 2026-10-07: "B", leave it off the
 * proposal rather than print the one line signing would bill. A typed zero
 * is no leftover: it asks for nothing.
 */
export function presentedSchedule(
  document: PresentedDocument,
  chosen?: Chosen,
  chosenUpgrades: readonly string[] = [],
): PresentedScheduleRow[] | null {
  const summary = document.sections.find(
    (section): section is SummarySection => section.id === "summary",
  );
  if (!summary?.deposit) return null;
  const bought = boughtPackages(document, chosen);
  if (bought === null) return null;
  // The first plan among what was bought: one customer, one plan.
  const plan = bought.find((pack) => pack.plan)?.plan ?? null;
  if (!scheduleWorthPrinting(bought, plan)) return null;

  const upgrades = upgradesTotal(document, chosen, chosenUpgrades);
  const options = bought.map((pack, index) => ({
    total: index === 0 ? addCents(pack.total, upgrades) : pack.total,
    typedFirst: pack.typedFirstPayment ?? null,
    schedule: pack.schedule ?? [],
  }));
  const contract = addCents(...options.map((option) => option.total));
  if (contract <= 0) return null;

  const triggerFor = (slot: number): string => {
    for (const option of options) {
      const text = option.schedule.find(
        (row) => row.slot === slot,
      )?.triggerText;
      if (text) return text;
    }
    return defaultTriggerText(slot);
  };
  const row = (slot: number, amount: Cents): PresentedScheduleRow =>
    scheduleRow(slot, amount, plan, triggerFor);

  // Jonathon, 2026-10-07: an amount still typed in an earlier payment of a
  // job under the split is the error the estimate screen shows, and the
  // proposal leaves the schedule off until the rep clears it.
  const leftover = options.some(
    (option) =>
      option.total < PAYMENT_SPLIT_THRESHOLD &&
      option.schedule.some(
        (r) => r.slot !== FINAL_SLOT && r.amount != null && r.amount > 0,
      ),
  );
  if (leftover) return null;

  if (contract < PAYMENT_SPLIT_THRESHOLD) {
    return [row(FINAL_SLOT, contract)];
  }

  // A figure typed on an option under the split is a leftover its own
  // schedule refuses, not a decision — the same reading `signingFirstPayment`
  // gives slot 1.
  const typedAcross = (slot: number): Cents =>
    addCents(
      ...options
        .filter((option) => option.total >= PAYMENT_SPLIT_THRESHOLD)
        .map(
          (option) =>
            option.schedule.find((r) => r.slot === slot)?.amount ??
            (0 as Cents),
        ),
    );
  const first = signingFirstPaymentTyped(options).amount;
  const second = typedAcross(2);
  const third = typedAcross(3);
  const final = finalPayment(contract, first, second, third);
  if (final < 0) return null;

  return [
    ...(first > 0 ? [row(1, first)] : []),
    ...(second > 0 ? [row(2, second)] : []),
    ...(third > 0 ? [row(3, third)] : []),
    row(FINAL_SLOT, final),
  ];
}
